import React, { useEffect, useRef, useCallback, useState, useMemo, useImperativeHandle } from 'react';
import { StyleSheet, Platform, Animated, View, FlatList, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import * as Device from 'expo-device';
import { useTheme } from '@/context/ThemeContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getShadowThemeStyles } from '@/constants/Colors';
import { useAuthStore, useRouteStore, useChatStore, useUIStore, useToastStore, useAgentStore, useModalStore, useChatInputStore, useVoiceModeStore, useToolStore, useModelStore, useCoworkerStore } from '@/store';
import ChatInput from '@/components/ChatInput/ChatInput';
import Message from '@/components/Messages/Message';
import MessageGroup from '@/components/Messages/MessageGroup';
import ConversationSummaryPill from '@/components/Messages/ConversationSummaryPill';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import { useAuthPresentation } from '@/hooks/useRequireAuth';
import useAppActive from '@/hooks/useAppActive';
import socketManager from '@/lib/socket';
import { isDesktopLinuxWeb } from '@/lib/desktopLinux';
import { chatsApi } from '@/lib/api';
import voiceModeService from '@/lib/voiceMode';
import Button from '@/components/Buttons/Button';
import Text from '@/components/Typography/Text';
import MessageWaiting from '@/components/Messages/MessageWaiting';
import MessageGhostUpsell from '@/components/Messages/MessageGhostUpsell';
import MessageDateSeparator from '@/components/Messages/MessageDateSeparator';
import PlusMessageLockBanner from '@/components/Messages/PlusMessageLockBanner';
import PlusLockedMessageCeiling from '@/components/Messages/PlusLockedMessageCeiling';
import MediaGenerationSuggestions from '@/components/Messages/MediaGenerationSuggestions';
import FollowUpSuggestions from '@/components/Messages/FollowUpSuggestions';
import ToolIntentSuggestions from '@/components/ToolIntentSuggestions';
import WorkModeIntentSuggestions from '@/components/WorkModeIntentSuggestions';
import WorkModeSuggestions from '@/components/Messages/WorkModeSuggestions';
import useToolIntentSuggestions from '@/hooks/useToolIntentSuggestions';
import useWorkModeIntentSuggestions from '@/hooks/useWorkModeIntentSuggestions';
import useCollectionSuggestion from '@/hooks/useCollectionSuggestion';
import useRequireAuth from '@/hooks/useRequireAuth';
import CollectionSuggestionBanner from '@/components/CollectionSuggestionBanner';
import {
  resolveImageRequestUserQuery,
  resolveImageRequestUserQueryForGroup,
} from '@/constants/utils/tool_intent_matcher';
import {
  resolveWorkModeSuggestion,
  getFileIds,
} from '@/constants/utils/work_mode_intent_matcher';
import { startWorkFromPrompt } from '@/lib/startWorkFromPrompt';
import reportWorkLaunchError from '@/lib/reportWorkLaunchError';
import { isWorkModeDisabledForUser } from '@/lib/workModeAvailability';
import {
  resolveMediaGenerationContext,
  resolveMediaGenerationContextForGroup,
} from '@/constants/utils/media_model_suggestions';
import { isArtifactSidebarType, isDesktopEditorSidebarType, maybeAutoOpenVideoProject, openMessageFile } from '@/constants/utils/files';
import CardExpirationWarningMessage from '@/components/Messages/CardExpirationWarningMessage';
import { CHAT_INPUT_STYLE, PLUS_MONTHLY_CREDITS_DISPLAY, MESSAGE_WIDTH_AMOUNTS, getNavigationSidebarWidth, getEffectiveContextSidebarWidth } from '@/constants/config';
import ChatInputContainer from '@/components/ChatInput/ChatInputContainer';
import CoworkerLivePanel from '@/components/Coworker/CoworkerLivePanel';
import { WorkApprovalCard, WorkPlanCard, WorkQuestionCard } from '@/components/Coworker/WorkActionCards';
import WorkTaskBoundaryCard from '@/components/Coworker/WorkTaskBoundaryCard';
import WorkTaskStartedNotice from '@/components/Coworker/WorkTaskStartedNotice';
import WorkAppear from '@/components/Coworker/WorkAppear';
import WorkSignInComposer from '@/components/Coworker/WorkSignInComposer';
import WorkFollowUpQueue from '@/components/Coworker/WorkFollowUpQueue';
import BrowserViewer from '@/components/Coworker/BrowserViewer';
import { WorkLiveProvider, useWorkLiveValue } from '@/components/Coworker/WorkLiveContext';
import { WorkNotesVisibilityProvider } from '@/components/Coworker/WorkNotesVisibility';
import { WorkLiveActivityRow, WorkTranscriptMessage, WorkUserTranscriptRow } from '@/components/Coworker/WorkTranscriptRows';
import WorkUpgradeNotice from '@/components/Coworker/WorkUpgradeNotice';
import {
  buildWorkRuns,
  buildWorkTurns,
  workMessageProjectionKey,
  workSourcesFromEvents,
  workUserMessageForDisplay,
  WORK_FOLLOWUP_PLACEHOLDER,
  WORK_STEER_PLACEHOLDER,
  shouldShowWorkLiveActivity,
  shouldShowWorkPinnedPlan,
  workPlanAttachIndex,
  WORK_THINKING_STATUS,
} from '@/constants/work';
import { isWorkFollowUpQueueMessage } from '@/constants/work_followup_queue';
import { useCoworkerChat } from '@/hooks/useCoworkerChat';
import { useConversationAgentHydration } from '@/hooks/useMessageAgents';
import { useWorkModeHandover } from '@/hooks/useWorkModeHandover';
import { hasAvailableCredits, canAccessPlusOnlyModel, isModelLockedForUser, openModelAccessPaywall, openCreditsRecoveryModal } from '@/constants/utils/trial';
import BannerPlacement from '@/components/Banner/BannerPlacement';
import ChatMessagesSkeleton from '@/components/Placeholders/ChatMessagesSkeleton';
import ChatLoadingState from '@/components/Placeholders/ChatLoadingState';
import ConversationTimelineRail from '@/components/Chat/ConversationTimelineRail';
import ConversationFindBar from '@/components/Chat/ConversationFindBar';
import SourcesOutputsPanel, { PANEL_TOP_OFFSET as SOURCES_OUTPUTS_PANEL_TOP } from '@/components/Chat/SourcesOutputsPanel';
import { resolveSourcesOutputsLayout } from '@/components/Chat/sourcesOutputsLayout';
import ConversationForkOrigin from '@/components/Chat/ConversationForkOrigin';
import SideChatLayout from '@/components/Chat/SideChatLayout';
import markdownToPlainText from '@/constants/utils/markdown_to_plain_text';
import { collectSourcesAndOutputs } from '@/constants/utils/sources_outputs';
import { collectVideoStudioInventory, loadVideoStudioScript } from '@/lib/videoStudioInventory';
import {
  buildVideoProject,
  firstVideoProjectMedia,
  conversationMediaStudio,
  isImageMedia,
  isObsoleteVideoFollowUp,
  videoProjectReviewGate,
  workVideoReviewQuestion,
} from '@/constants/utils/video_project';
import { useTranslation } from '@/hooks/useTranslation';
import useWorkChatTasks from '@/hooks/useWorkChatTasks';
import { saveLastChatScroll, clearLastChatScroll } from '@/lib/lastRoute';
import { findGroupedMessageIndex, scrollMessageNodeIntoView, scrollMessageNodeToStart } from '@/constants/utils/conversation_find';
import { isMobileReadingChat, shouldHoldChatScroll } from '@/constants/utils/chat_scroll_policy';
import {
  findStreamingWorkAssistant,
  getWorkStreamScrollMode,
  shouldRestoreStudioScroll,
  shouldShowScrollToBottomButton,
  WORK_STREAM_SCROLL,
} from '@/constants/utils/work_stream_scroll';

const EMPTY_COMPOSER_FILES = [];

// Sources / Outputs card (232px) + its 12px right offset + breathing room
// before the message column starts. Keep in sync with the panel's own sizing.

function MessageHighlightWrapper({ messageIds, children }) {
  const highlightedId = useUIStore(state => state.highlightedMessageId);
  const searchQuery = useUIStore(state => state.highlightedSearchQuery);
  const persistHighlight = useUIStore(state => state.highlightedMessagePersist);
  const clearHighlightedMessageId = useUIStore(state => state.clearHighlightedMessageId);
  const { colors } = useTheme();
  const ids = messageIds || [];
  const isHighlighted = !!highlightedId && ids.includes(highlightedId);
  const animatedOpacity = useRef(new Animated.Value(0)).current;
  const isWeb = Platform.OS === 'web';
  const domId = isWeb ? `msg-hl-${(isHighlighted && highlightedId) || ids[0] || 'x'}` : undefined;

  useEffect(() => {
    if (!isHighlighted) return;

    if (isWeb && typeof document !== 'undefined') {
      if (!document.getElementById('search-hl-css')) {
        const s = document.createElement('style');
        s.id = 'search-hl-css';
        s.textContent = `
          @keyframes searchHlPulse { 0%{opacity:0} 5%{opacity:1} 80%{opacity:1} 100%{opacity:0} }
          mark.search-hl {
            background-color: rgba(250,200,21,0.35) !important;
            color: inherit !important; border-radius: 3px !important;
            padding: 1px 2px !important; display: inline !important;
            box-decoration-break: clone !important; -webkit-box-decoration-break: clone !important;
            box-shadow: 0 0 0 3px rgba(250,200,21,0.25) !important;
            animation: searchHlPulse 6s ease-out forwards !important;
          }
          mark.search-hl-persist {
            animation: none !important;
            opacity: 1 !important;
          }`;
        document.head.appendChild(s);
      }

      const q = searchQuery?.toLowerCase();
      let applied = false;

      const tryApply = () => {
        if (applied || !q) return true;
        const el = document.getElementById(domId);
        if (!el?.textContent) return false;
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
        while (walker.nextNode()) {
          const tv = walker.currentNode.nodeValue;
          const idx = tv?.toLowerCase().indexOf(q);
          if (idx == null || idx === -1) continue;
          const node = walker.currentNode;
          const p = node.parentNode;
          if (!p) continue;
          const frag = document.createDocumentFragment();
          if (idx > 0) frag.appendChild(document.createTextNode(tv.slice(0, idx)));
          const mark = document.createElement('mark');
          mark.className = persistHighlight ? 'search-hl search-hl-persist' : 'search-hl';
          mark.textContent = tv.slice(idx, idx + searchQuery.length);
          frag.appendChild(mark);
          if (idx + searchQuery.length < tv.length) frag.appendChild(document.createTextNode(tv.slice(idx + searchQuery.length)));
          p.replaceChild(frag, node);
          applied = true;
          return true;
        }
        return false;
      };

      let tries = 0;
      const poll = setInterval(() => {
        if (tryApply() || ++tries > 40) clearInterval(poll);
      }, 150);
      tryApply();

      const expire = persistHighlight ? null : setTimeout(() => {
        clearInterval(poll);
        try {
          const el = document.getElementById(domId);
          if (el) el.querySelectorAll('mark.search-hl').forEach(m => {
            const p = m.parentNode;
            if (p) { p.replaceChild(document.createTextNode(m.textContent || ''), m); p.normalize(); }
          });
        } catch (_) {}
        clearHighlightedMessageId();
      }, 7000);

      return () => {
        clearInterval(poll);
        if (expire) clearTimeout(expire);
        try {
          const el = document.getElementById(domId);
          if (el) el.querySelectorAll('mark.search-hl').forEach(m => {
            const p = m.parentNode;
            if (p) { p.replaceChild(document.createTextNode(m.textContent || ''), m); p.normalize(); }
          });
        } catch (_) {}
      };
    }

    if (persistHighlight) {
      animatedOpacity.setValue(1);
      return undefined;
    }

    animatedOpacity.setValue(0);
    Animated.sequence([
      Animated.timing(animatedOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
      Animated.delay(4000),
      Animated.timing(animatedOpacity, { toValue: 0, duration: 800, useNativeDriver: true }),
    ]).start(() => clearHighlightedMessageId());
  }, [isHighlighted, persistHighlight, searchQuery, highlightedId, domId, isWeb, animatedOpacity, clearHighlightedMessageId]);

  if (isWeb) {
    return <View nativeID={domId}>{children}</View>;
  }

  if (isHighlighted) {
    return (
      <View style={{ position: 'relative' }}>
        <Animated.View
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: -2, left: -4, right: -4, bottom: -2,
            backgroundColor: colors.accent_color_primary || '#FFD700',
            borderWidth: 1.5,
            borderColor: colors.accent_color_primary || '#FFD700',
            borderRadius: 8,
            opacity: animatedOpacity.interpolate({ inputRange: [0, 1], outputRange: [0, 0.18] }),
          }}
        />
        {children}
      </View>
    );
  }

  return children;
}

const baseStyles = StyleSheet.create({
  content: {
    flex: 1,
    position: 'relative',
    flexDirection: 'row',
    overflow: 'hidden',
    ...(Platform.OS === 'web' && { minHeight: 0 }),
  },
  messagesContainer: {
    flex: 1,
    position: 'relative',
    flexDirection: 'row',
    ...(Platform.OS === 'web' && { minHeight: 0 }),
  },
  transcriptContainer: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    ...(Platform.OS === 'web' && { minHeight: 0 }),
  },
});

function renderListSlot(Component) {
  if (!Component) return null;
  if (typeof Component === 'function') return <Component />;
  return Component;
}

/**
 * RN Web FlatList windowing drops unmeasured Work rows and then treats the
 * short content height as the end of the thread. A plain ScrollView keeps
 * every row mounted so scrolling back down reaches the latest messages.
 */
function WebWorkTranscriptList({
  listRef,
  data,
  renderItem,
  keyExtractor,
  onScroll,
  onContentSizeChange,
  onLayout,
  contentContainerStyle,
  ListHeaderComponent,
  ListFooterComponent,
  style,
}) {
  const scrollRef = useRef(null);
  const nodeOf = () => scrollRef.current?.getScrollableNode?.() || scrollRef.current;

  useImperativeHandle(listRef, () => ({
    scrollToOffset: ({ offset = 0, animated = false } = {}) => {
      const node = nodeOf();
      if (node?.scrollTo) node.scrollTo({ y: offset, animated });
      else if (node) node.scrollTop = offset;
    },
    scrollToEnd: ({ animated = false } = {}) => {
      const node = nodeOf();
      const top = node?.scrollHeight || 1e8;
      if (node?.scrollTo) node.scrollTo({ y: top, animated });
      else if (node) node.scrollTop = top;
    },
    scrollToIndex: ({ index = 0, animated = false } = {}) => {
      const node = nodeOf();
      const child = node?.querySelector?.(`#work-row-${index}`);
      if (child && node) {
        const next = Math.max(0, child.offsetTop - 12);
        if (node.scrollTo) node.scrollTo({ y: next, animated });
        else node.scrollTop = next;
      }
    },
  }), []);

  return (
    <ScrollView
      ref={scrollRef}
      style={[{ flex: 1, minHeight: 0 }, style]}
      contentContainerStyle={contentContainerStyle}
      onScroll={onScroll}
      scrollEventThrottle={16}
      onContentSizeChange={onContentSizeChange}
      onLayout={onLayout}
    >
      {renderListSlot(ListHeaderComponent)}
      {(data || []).map((item, index) => (
        <View key={keyExtractor(item, index)} nativeID={`work-row-${index}`}>
          {renderItem({ item, index })}
        </View>
      ))}
      {renderListSlot(ListFooterComponent)}
    </ScrollView>
  );
}

const ChatView = React.memo(({
  conversationId,
  chat: chatProp,
  isLoading,
  error,
  isCompact = false,
  onFocus,
}) => {
  const id = Array.isArray(conversationId) ? conversationId[0] : conversationId;
  const storeChat = useChatStore((state) => (id ? state.chats.find((c) => c.id === id) || null : null));
  const chat = storeChat || chatProp;
  const router = useRouter();
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const requireAuth = useRequireAuth();
  const themeStyles = getShadowThemeStyles(theme);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const fetchArchivedMessages = useChatStore(state => state.fetchArchivedMessages);
  const isChatInStore = useChatStore(state => state.chats.some(c => c.id === conversationId));
  const messageWidth = useUIStore(state => state.messageWidth);
  const chatInputStyle = useUIStore(state => state.chatInputStyle);
  const { setCurrentLibraryId } = useRouteStore(state => state.actions);
  const isAuthenticated = useAuthPresentation();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const user = useAuthStore(state => state.user);
  const isPlus = user?.flags?.isPlus;
  const workModeDisabled = isWorkModeDisabledForUser(user);
  const isHeldForReview = user?.flags?.heldForReview === true;
  const canUseGatedModels = canAccessPlusOnlyModel(user);
  // Mirrors the backend inference gate (permissions_validator_service): users
  // flagged as requiring verification can't send until their email is verified.
  const needsEmailVerification = useAuthStore(state =>
    !!state.user?.flags?.isEmailVerificationRequired && !state.user?.flags?.isEmailVerified
  );
  // Plus/admin users are exempt from the SMS gate on the backend, so don't
  // UI-block them here either.
  const needsPhoneVerification = useAuthStore(state =>
    !!state.user?.flags?.smsVerificationRequired && !state.user?.flags?.isPhoneVerified
    && !state.user?.flags?.isPlus && !state.user?.flags?.isAdmin
  );
  const userId = useAuthStore(state => state.user?.id);
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable ?? 0);
  const getAgentById = useAgentStore(state => state.getAgentById);
  const { isXS, showsScrollIndicator, windowWidth } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const flatListRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const scrollElementRef = useRef(null);
  const getScrollElement = useCallback(() => {
    if (Platform.OS !== 'web' || !messagesContainerRef.current) return null;
    const cached = scrollElementRef.current;
    if (cached && cached.isConnected && cached.offsetHeight >= 100) return cached;
    const el = [...(messagesContainerRef.current.querySelectorAll('*') || [])].find((e) => {
      if (e.offsetHeight < 100) return false;
      const s = window.getComputedStyle(e);
      return s.overflowY === 'auto' || s.overflowY === 'scroll';
    }) || null;
    scrollElementRef.current = el;
    return el;
  }, []);
  const inputContainerRef = useRef(null);
  // Always-latest pointer to requestPinToBottom so handlers defined *above* its
  // declaration (e.g. handleSend) can invoke it without a temporal-dead-zone
  // forward reference in their dependency array.
  const requestPinToBottomRef = useRef(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Detected after mount so SSR / first paint match Mac/Windows (windowSize 101).
  // Linux then tightens virtualization — see flatListProps.
  const [isLinuxDesktopWeb, setIsLinuxDesktopWeb] = useState(false);
  useEffect(() => {
    if (Platform.OS === 'web') setIsLinuxDesktopWeb(isDesktopLinuxWeb());
  }, []);
  const lastMessageId = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const showScrollButtonRef = useRef(false);
  const showScrollButtonRafRef = useRef(0);
  const pendingShowScrollButtonRef = useRef(null);
  // RN Web fires onContentSizeChange during the layout pass. setState there
  // after a Work follow-up (new bubble + start-pin + live row) nested updates
  // until React hit Maximum update depth. Coalesce onto the next frame and
  // skip when the visibility did not change.
  const scheduleShowScrollButton = useCallback((next) => {
    const value = !!next;
    pendingShowScrollButtonRef.current = value;
    if (showScrollButtonRafRef.current) return;
    if (showScrollButtonRef.current === value) return;
    const apply = () => {
      showScrollButtonRafRef.current = 0;
      const scheduled = pendingShowScrollButtonRef.current;
      if (showScrollButtonRef.current === scheduled) return;
      showScrollButtonRef.current = scheduled;
      setShowScrollButton(scheduled);
    };
    if (typeof requestAnimationFrame === 'function') {
      showScrollButtonRafRef.current = requestAnimationFrame(apply);
      return;
    }
    apply();
  }, []);
  const scrollButtonAnimation = useRef(new Animated.Value(0)).current;
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const keyboardHeight = useComposerKeyboardHeight();
  const [inputContainerHeight, setInputContainerHeight] = useState(0);
  const isAtBottomRef = useRef(true);
  // Follow the live tail until the user scrolls up. Normal mobile chat opts
  // into this only through the down-arrow; sending preserves reading position.
  const followLiveRef = useRef(false);
  const chatScrollStateRef = useRef({});
  const studioUserTurnRef = useRef(null);
  const scrollOffsetRef = useRef(0);
  const listLayoutHeightRef = useRef(0);
  const contentHeightRef = useRef(0);
  const lastScrollTime = useRef(0);
  // A content reflow can move FlatList's reported offset upward even when the
  // user did not touch the transcript. Keep a short-lived, explicit gesture
  // signal so those layout corrections do not disable follow-live.
  const userScrollIntentUntilRef = useRef(0);
  // True from onScrollBeginDrag until the drag (and any momentum) ends, so a
  // long finger-drag or flick still counts as user intent past the 1s window.
  const isUserScrollingRef = useRef(false);
  const lastDownwardScrollAtRef = useRef(0);
  // When the last programmatic pin fired. Its scroll event lands a frame or
  // two later; if the user has started dragging in between, that echo reads
  // as "scrolled down into the tail" and re-armed follow-live, so the next
  // token flush yanked them back to the bottom. Work streams a paced reveal
  // every flush, so there was almost always an echo in flight.
  const lastProgrammaticPinAtRef = useRef(0);
  // Work long-reply: id of the streaming message we already aligned to the
  // top of the viewport. Re-pinning every token would yank a reader who
  // scrolled down into the essay; jump-to-live still follows the tail.
  const workStreamPinnedIdRef = useRef(null);
  const groupedMessagesRef = useRef([]);
  const streamingWorkRef = useRef(null);
  const keyboardAnimatingRef = useRef(false);
  // Pin-to-bottom mechanism: persists through lazy-rendering content growth.
  // Long chats render messages progressively, so a single scrollToEnd call stops
  // short of the real bottom. We keep re-pinning on every content-size change
  // until content stabilizes (debounce) or a hard deadline passes.
  const wantsBottomRef = useRef(false);
  const bottomStabilizeTimerRef = useRef(null);
  const bottomMaxTimerRef = useRef(null);
  // Scheduled follow-up scroll attempts (rAF + short timeouts) that re-assert
  // the bottom after the current render commits and before the round-tripped
  // message arrives.
  const bottomRepinHandlesRef = useRef([]);
  const initialScrolledIdRef = useRef(null);
  const archiveScrollHydrationRef = useRef({ key: null, didTargetedLoad: false, loadsRemaining: 0 });
  const prependCompensationRef = useRef(null);
  // Anchor-pin mechanism (cold-start restore): after the initial
  // scroll-to-anchor, late-rendering content (markdown, code, math, images)
  // keeps changing row heights above the anchor, shoving it around the
  // viewport. Mirror of the bottom-pin: silently re-pin the anchor on every
  // content-size change until layout stabilizes or the deadline passes.
  // { messageId, viewPosition, until } | null
  const anchorPinRef = useRef(null);
  // Settle veil (native): opening a long chat positions the list (bottom-pin
  // or restore anchor) while row heights are still estimates; every async
  // measurement (markdown / code / images) then visibly shakes the list as
  // the pin corrects. Instead of showing that, we hide the list (opacity 0)
  // during initial positioning and fade it in once content has been quiet
  // briefly — so the user only ever sees the final, stable position.
  const listSettleOpacity = useRef(new Animated.Value(1)).current;
  const settleVeilActiveRef = useRef(false);
  const settleVeilQuietTimerRef = useRef(null);
  const settleVeilMaxTimerRef = useRef(null);
  // Mounts the chat skeleton overlay while the veil is up; the skeleton's
  // opacity is the inverse of the list's, giving a cross-fade on reveal.
  const [isSettleVeilUp, setIsSettleVeilUp] = useState(false);
  // Work tool trails attach once the run's event log arrives; the settle veil
  // waits on this so they don't pop in after the list is revealed.
  const workEventsReadyRef = useRef(true);
  // Lets the web scroll-intent listener (declared above endListSettleVeil)
  // drop the veil without a temporal-dead-zone forward reference.
  const endListSettleVeilRef = useRef(null);
  const settleSkeletonOpacity = useMemo(
    () => listSettleOpacity.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }),
    [listSettleOpacity],
  );
  const pendingScrollRequest = useUIStore(state => state.pendingScrollRequest);
  const requestScrollToMessage = useUIStore(state => state.requestScrollToMessage);
  const clearPendingScrollRequest = useUIStore(state => state.clearPendingScrollRequest);
  const pendingScrollToTopRequest = useUIStore(state => state.pendingScrollToTopRequest);
  const clearPendingScrollToTopRequest = useUIStore(state => state.clearPendingScrollToTopRequest);
  const deviceType = Device.deviceType;
  const isMobileOrTablet = deviceType === Device.DeviceType.PHONE || deviceType === Device.DeviceType.TABLET;
  const showDisclaimer = useMemo(() => {
    if (isCompact) return false;
    return isXS || isMobileOrTablet;
  }, [isXS, isMobileOrTablet, isCompact]);
  const [isNearTop, setIsNearTop] = useState(true);

  // ── Scroll-position memory ────────────────────────────────────────────────
  // Remember the top-most visible message of the most-recently-active chat so a
  // cold-start restore (see useRestoreLastRoute) can drop the user back where
  // they were reading instead of at the bottom. We persist the anchor *message
  // id* (not a raw offset) so restoration can reuse the chat's robust
  // scroll-to-message flow, which already tolerates async layout growth.
  const idRef = useRef(id);
  idRef.current = id;
  const isCompactRef = useRef(isCompact);
  isCompactRef.current = isCompact;
  const scrollPersistTimerRef = useRef(null);
  // Top-most visible real message; drives the timeline rail's active tick.
  const [viewableAnchorId, setViewableAnchorId] = useState(null);
  // Mirror of viewableAnchorId for callbacks that must stay referentially
  // stable (handleLoadEarlierMessages) while still reading the latest anchor.
  const viewableAnchorIdRef = useRef(null);
  // Work threads never render the timeline rail, so the anchor only needs to
  // live in the ref there. Skipping the state update keeps a fast scroll from
  // re-rendering (and re-measuring) the whole transcript on every viewability
  // change, which is what makes media-heavy Work rows shuffle mid-gesture.
  const tracksAnchorInStateRef = useRef(true);
  const viewabilityConfigRef = useRef({ itemVisiblePercentThreshold: 10, minimumViewTime: 200 });
  const onViewableItemsChangedRef = useRef((info) => {
    const viewableItems = info?.viewableItems;
    if (isCompactRef.current) return;
    if (!viewableItems || viewableItems.length === 0) return;

    // First viewable item that maps to a real message (skip synthetic rows like
    // the summary pill, waiting indicator, ghost / card-expiration messages).
    let anchorId = null;
    for (let i = 0; i < viewableItems.length; i += 1) {
      const item = viewableItems[i]?.item;
      if (!item) continue;
      if (item.type === 'group') {
        anchorId = item.messages?.[0]?.id || null;
      } else if (item.role && item.id && !item.type
        && item.id !== 'inferencing-waiting' && item.id !== 'ghost-message') {
        anchorId = item.id;
      }
      if (anchorId) break;
    }
    if (!anchorId) return;

    viewableAnchorIdRef.current = anchorId;
    if (tracksAnchorInStateRef.current) setViewableAnchorId(anchorId);

    const convId = idRef.current;
    if (!convId || convId === 'new' || String(convId).startsWith('new-')) return;

    if (scrollPersistTimerRef.current) clearTimeout(scrollPersistTimerRef.current);
    scrollPersistTimerRef.current = setTimeout(() => {
      // At the bottom → forget any anchor so the next restore lands naturally.
      if (isAtBottomRef.current) {
        clearLastChatScroll();
      } else {
        saveLastChatScroll(idRef.current, anchorId);
      }
    }, 600);
  });

  useEffect(() => () => {
    if (scrollPersistTimerRef.current) clearTimeout(scrollPersistTimerRef.current);
    if (showScrollButtonRafRef.current && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(showScrollButtonRafRef.current);
      showScrollButtonRafRef.current = 0;
    }
  }, []);

  const effectiveChatInputStyle = isCompact ? CHAT_INPUT_STYLE.FIXED : chatInputStyle;
  const isDetailed = effectiveChatInputStyle === CHAT_INPUT_STYLE.DETAILED;

  const updateInputContainerHeight = useCallback((height) => {
    const nextHeight = Math.ceil(height || 0);
    setInputContainerHeight((prev) => (Math.abs(prev - nextHeight) > 1 ? nextHeight : prev));
  }, []);

  const handleInputContainerLayout = useCallback((event) => {
    updateInputContainerHeight(event?.nativeEvent?.layout?.height);
  }, [updateInputContainerHeight]);

  // RN Web's onLayout can run before Firefox has settled Linux font metrics or
  // after only part of the composer stack has rendered. Observe the actual DOM
  // box as well so list padding follows textarea, banner, and font reflows.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof ResizeObserver === 'undefined') return undefined;
    const node = inputContainerRef.current;
    if (!node) return undefined;

    const observer = new ResizeObserver((entries) => {
      updateInputContainerHeight(entries[0]?.contentRect?.height);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [updateInputContainerHeight, isAuthenticated, chat?.isOwner]);

  // Voice mode state
  const isVoiceModeActive = useVoiceModeStore(state => state.isActive);
  const voiceModeConversationId = useVoiceModeStore(state => state.conversationId);
  const stopVoiceMode = useVoiceModeStore(state => state.actions.stopVoiceMode);

  // Card expiration warning state
  const cardExpirationWarning = useAuthStore(state => state.cardExpirationWarning);
  const clearCardExpirationWarning = useAuthStore(state => state.clearCardExpirationWarning);
  const [showCardExpirationWarning, setShowCardExpirationWarning] = useState(false);

  useEffect(() => {
    if (
      cardExpirationWarning?.shouldDisplay &&
      cardExpirationWarning?.conversationId === id
    ) {
      setShowCardExpirationWarning(true);
    }
  }, [cardExpirationWarning?.shouldDisplay, cardExpirationWarning?.conversationId, id]);

  const handleDismissCardExpirationWarning = useCallback(() => {
    setShowCardExpirationWarning(false);
    clearCardExpirationWarning();
  }, [clearCardExpirationWarning]);

  // Auto-end voice mode when unmounting this view
  useEffect(() => {
    const currentConversationId = id;
    return () => {
      const voiceStore = useVoiceModeStore.getState();
      if (voiceStore.isActive && voiceStore.conversationId === currentConversationId) {
        console.log('[ChatView] Unmounting voice conversation, ending voice mode');
        voiceModeService.endSession().catch(err => {
          console.warn('[ChatView] Error ending voice session:', err);
        });
        voiceStore.actions.stopVoiceMode();
      }
    };
  }, [id]);

  const isOwner = chat?.isOwner;
  const chatTitle = chat?.title;

  // Coworker unification (docs/COWORKER_CONVERSATION_UNIFICATION.md): a
  // coworker thread masquerades as a live conversation — the hook keeps the
  // chat's isInferencing flag in sync with task liveness so the composer
  // (Stop button, send gating) and list UI work unchanged.
  const coworker = useCoworkerChat({ chat, conversationId: id });
  useConversationAgentHydration(id);
  // The intent engine can upgrade a chat to Work mid-send. The hook above
  // joins the run immediately; the UI switches at the trough of a short fade
  // so the composer and transcript change together instead of popping.
  // See useWorkModeHandover.
  const handleWorkHandoverSwap = useCallback((toWork) => {
    if (!toWork || !id) return;
    // The chat "thinking" bubble held the spot through the fade; releasing it
    // here lets the Work activity line take over in the same render.
    useChatStore.getState().updateLocalChatDetails(id, {
      isInferencingWaiting: false,
      pendingInferenceStatus: undefined,
      pendingInferenceStage: undefined,
    });
  }, [id]);
  const { workMode: isCoworkerChat, fadeStyle: workHandoverStyle } = useWorkModeHandover(
    coworker.isCoworkerChat,
    { onSwap: handleWorkHandoverSwap },
  );
  const coworkerSend = coworker.send;
  const coworkerEnqueue = coworker.enqueueFollowUp;
  const coworkerSendFollowUpNow = coworker.sendFollowUpNow;
  const coworkerStop = coworker.stop;
  const coworkerHasSnapshot = coworker.hasSnapshot;
  const coworkerIsActive = coworker.isActive;

  // A waiting_on_user settle keeps the card up after the runner has already
  // stopped. Answering the card must resume with that text or the send looks
  // like it did nothing.
  const handleWorkQuestionAnswered = useCallback((text, { skipped } = {}) => {
    if (coworkerIsActive) return;
    const message = String(text || '').trim()
      || (skipped ? 'Skip — pick a sensible default and keep going.' : '');
    if (!message) return;
    // Preserve the actual answer, including any instruction to wait. Take
    // choices already live in durable Work events; the local UI cache may
    // predate a newer selection made in chat or another studio window.
    void coworkerSend(message);
  }, [coworkerIsActive, coworkerSend]);

  useEffect(() => {
    if (!isCoworkerChat) return undefined;
    return () => {
      try {
        const { Image } = require('expo-image');
        Image.clearMemoryCache?.();
      } catch { /* optional — native web cache only */ }
    };
  }, [isCoworkerChat, id]);

  const isInferencing = chat?.isInferencing || (isCoworkerChat && coworker.isActive);
  const isInferencingWaiting = chat?.isInferencingWaiting;
  const mobileReadingChat = isMobileReadingChat(Platform.OS, isCoworkerChat);

  // HTTP-initiated runs (prompts) can miss socket events if the client
  // navigates after the backend already finished. Poll until the server
  // clears flags.isInferencing, then refresh the thread.
  //
  // Gated on `isAppActive` so a backgrounded/hidden tab doesn't keep polling
  // every 4s forever. When the tab returns to the foreground the effect
  // re-runs and fires an immediate poll, so we pick up the finished state
  // without waiting out the first 4s tick.
  const isAppActive = useAppActive();
  useEffect(() => {
    // Coworker threads: liveness comes from the task's SSE feed
    // (useCoworkerChat), not the conversation-inference flags this poll
    // reconciles — polling here would never see the flag clear and loop.
    if (isCoworkerChat) return;
    if (!id || (!isInferencing && !isInferencingWaiting) || !isAppActive) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const status = await chatsApi.getConversationStatus(id);
        if (cancelled || status?.isInferencing) return;
        const data = await chatsApi.getConversationById(id);
        if (cancelled || !data) return;
        useChatStore.getState().addChats([data]);
      } catch (_) {
        // Best-effort fallback — socket path is primary.
      }
    };

    // Immediate poll on becoming active — the server may have finished while
    // we were paused, and we don't want to wait the full 4s for the first
    // status check after focus/foreground.
    poll();
    const interval = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id, isInferencing, isInferencingWaiting, isAppActive, isCoworkerChat]);

  // Follow-up suggestions state
  const followUpSuggestions = useChatStore(state => state.followUpSuggestions[id]);
  const clearFollowUpSuggestions = useChatStore(state => state.clearFollowUpSuggestions);
  // Respect the user's opt-out instantly — even if the server emitted a set of
  // suggestions before the toggle was flipped, the UI should hide them now.
  const followUpSuggestionsEnabled = useAuthStore(
    state => state.user?.preferences?.chat?.followUpSuggestionsEnabled !== false
  );
  const showFollowUps = followUpSuggestionsEnabled
    && !isInferencing
    && !isInferencingWaiting
    && !(isCoworkerChat && coworker.isActive)
    && followUpSuggestions?.suggestions?.length > 0;

  // "Move this chat to <collection>?" suggestion (auto-categorization).
  // Rare by design (server-side throttled, once per chat ever), so when it is
  // present it takes priority over the follow-up suggestions strip.
  const {
    suggestion: collectionSuggestion,
    isResponding: isRespondingToCollectionSuggestion,
    accept: acceptCollectionSuggestion,
    dismiss: dismissCollectionSuggestion,
  } = useCollectionSuggestion(id);

  // Composer text drives which suggestion strip is visible above the input:
  // follow-ups when empty, tool-intent chips when a tool match is found.
  // Tool/memory upsells are suppressed while a desktop editor is open.
  const contextSidebarType = useUIStore(state => state.contextSidebarType);
  const isContextSidebarVisibleForSuggestions = useUIStore(state => state.isContextSidebarVisible);
  const isDesktopEditorOpen = isContextSidebarVisibleForSuggestions
    && isDesktopEditorSidebarType(contextSidebarType);
  const isVideoStudioOpen = isContextSidebarVisibleForSuggestions
    && contextSidebarType === 'video-project-preview';
  const isVideoStudioOpenRef = useRef(isVideoStudioOpen);
  isVideoStudioOpenRef.current = isVideoStudioOpen;
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const el = getScrollElement();
    if (!el) return undefined;
    const previous = el.style.overflowAnchor;
    el.style.overflowAnchor = isVideoStudioOpen ? 'none' : '';
    return () => {
      el.style.overflowAnchor = previous;
    };
  }, [getScrollElement, isVideoStudioOpen]);
  const { hasSuggestions: hasToolIntentSuggestions, message: composerText } = useToolIntentSuggestions(id);
  const {
    shouldSuggest: hasWorkModeIntentSuggestions,
    message: workComposerText,
    files: workComposerFiles,
  } = useWorkModeIntentSuggestions(id);
  const isComposerEmpty = !composerText.trim();
  const showCollectionSuggestionBar = !!collectionSuggestion
    && isComposerEmpty
    && !isInferencing
    && !isInferencingWaiting;
  const showFollowUpsBar = showFollowUps && isComposerEmpty && !showCollectionSuggestionBar;
  const showWorkIntentBar = hasWorkModeIntentSuggestions && !isDesktopEditorOpen && !isCoworkerChat;
  // Work replaces the tool chip when both match, so the composer doesn't
  // reserve room for a strip that ToolIntentSuggestions won't render.
  const showToolIntentBar = hasToolIntentSuggestions
    && !showWorkIntentBar
    && !isDesktopEditorOpen
    && !isCoworkerChat;
  const showSuggestionsBar = showFollowUpsBar || showToolIntentBar || showWorkIntentBar || showCollectionSuggestionBar;

  const fetchToolCatalog = useToolStore(state => state.fetchCatalog);
  const isFetchedToolCatalog = useToolStore(state => state.isFetchedCatalog);
  useEffect(() => {
    if (!isFetchedToolCatalog) {
      fetchToolCatalog().catch(() => {});
    }
  }, [id, isFetchedToolCatalog, fetchToolCatalog]);

  const agent = useMemo(() => {
    if (!chat?.agentId) return null;
    return getAgentById(chat.agentId);
  }, [chat?.agentId, getAgentById]);

  const isImageGeneration = agent?.outputTypes?.includes('image');

  useEffect(() => {
    setInitialLoadComplete(false);
  }, [id]);

  useEffect(() => {
    setShowCardExpirationWarning(false);
  }, [id]);

  // Set the chat input context when conversation changes
  const setContext = useChatInputStore(state => state.setContext);
  useEffect(() => {
    if (id) {
      setContext(id);
    }
  }, [id, setContext]);

  useEffect(() => {
    if (id && chat?.agentId && !isCoworkerChat) {
      useChatInputStore.getState().seedConversationAgent(id, chat.agentId);
    }
  }, [id, chat?.agentId, isCoworkerChat]);

  // Restore the conversation's last-used model in the composer once messages
  // are loaded. Input states are in-memory only, so after a refresh the model
  // selector falls back to the default text model / Auto even if the user was
  // chatting with e.g. GPT Image 2. Seeding is a no-op when the user already
  // made an explicit selection in this context (including explicit Auto).
  // Retry when models land in the store — addChats defers addModels, so the
  // first attempt often fails for image models that aren't in favorites yet.
  const historySeededIdRef = useRef(null);
  const chatMessageCount = chat?.messages?.length || 0;
  const modelStoreSize = useModelStore(state => Object.keys(state.models || {}).length);
  useEffect(() => {
    if (!id || chatMessageCount === 0) return;
    if (historySeededIdRef.current === id) return;
    const settled = useChatInputStore.getState().seedModelsFromConversationHistory(id);
    if (settled) {
      historySeededIdRef.current = id;
    }
  }, [id, chatMessageCount, modelStoreSize]);

  // User intent (sending, tapping scroll-to-bottom, or manually dragging) must
  // win over an in-flight cold-start scroll restore. On really long chats the
  // restore queued by useRestoreLastRoute stays active for several seconds —
  // it retries scrollToIndex and pages in archived history to reach the saved
  // mid-conversation anchor, and each retry calls clearBottomPin() +
  // scrollToIndex(anchor). That loop is what yanks the viewport back up to the
  // middle right after the user reaches (or sends to) the bottom. Dropping the
  // anchor pin and the pending scroll request for THIS conversation lets the
  // bottom-pin own the viewport. We deliberately leave the *persisted* anchor
  // alone: onViewableItemsChanged clears it once we settle at the bottom, while
  // a manual scroll-up keeps it so backgrounding mid-read still restores.
  const abortScrollRestore = useCallback(() => {
    anchorPinRef.current = null;
    const ui = useUIStore.getState();
    if (ui.pendingScrollRequest?.conversationId === idRef.current) {
      clearPendingScrollRequest();
    }
    if (ui.pendingScrollToTopRequest?.conversationId === idRef.current) {
      clearPendingScrollToTopRequest();
    }
  }, [clearPendingScrollRequest, clearPendingScrollToTopRequest]);

  const getModelById = useModelStore(state => state.getModelById);

  const findLockedSelectedModel = useCallback((modelIds) => {
    const ids = Array.isArray(modelIds) ? modelIds : [];
    for (const mid of ids) {
      const model = getModelById?.(mid) || useModelStore.getState().models?.[mid];
      if (model && isModelLockedForUser(user, model)) return model;
    }
    return null;
  }, [getModelById, user]);

  const handleSend = useCallback(async (formattedMessage) => {
    if (onFocus) onFocus();

    if (isHeldForReview) {
      openModal('AccountReviewHoldModal', { source: 'composer_send' });
      return false;
    }

    // Coworker thread: the composer routes to the task session — steer a
    // live run, or start a follow-up turn on an idle thread. Attachments
    // (formattedMessage.fileIds) are injected into the agent prompt as
    // fetch_url-able CDN URLs (validated server-side). coworkerSend inserts
    // the optimistic user bubble before the API call so the thread doesn't
    // sit empty until the agent replies. While live, Enter and Queue use
    // this path. Send now goes through handleSendNow.
    if (isCoworkerChat) {
      try {
        abortScrollRestore();
        followLiveRef.current = false;
        workStreamPinnedIdRef.current = null;
        if (coworker.isActive) {
          const ok = coworkerEnqueue(formattedMessage?.text, formattedMessage?.fileIds);
          return ok;
        }
        const ok = await coworkerSend(formattedMessage?.text, formattedMessage?.fileIds);
        return ok;
      } catch (err) {
        reportWorkLaunchError(err, {
          source: 'work_thread_follow_up',
          fallback: 'Failed to send',
        });
        return false;
      }
    }

    // No connection gate here: emitSendUserMessage queues the message when
    // offline (bubble shows a waiting-for-connection state) and flushes on
    // reconnect — a send should never dead-end on a mobile network blip.

    if (!isGhost && needsPhoneVerification) {
      openModal('VerifyPhoneModal', { source: 'composer_send' });
      return false;
    }

    if (!isGhost && needsEmailVerification) {
      openModal('VerifyEmailModal', { source: 'composer_send' });
      return false;
    }

    if (!isPlus && !isGhost && !hasAvailableCredits(totalAvailableCredits)) {
      openCreditsRecoveryModal(openModal, user, {
        source: 'composer_send',
        title: "Out of credits",
        subtitle: `You've used all your free credits. Upgrade to Plus for free default model messages and ${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits.`,
      });
      return false;
    }

    if (agent?.flags?.isPlusOnly && !isPlus) {
      openModal('PricingModal', {
        title: "Access Premium AI Agents",
        subtitle: "Upgrade to Plus to unlock premium AI agents with advanced capabilities and specialized knowledge."
      });
      return false;
    }

    const lockedModel = findLockedSelectedModel(formattedMessage?.modelIds);
    if (lockedModel) {
      openModelAccessPaywall(openModal, {
        model: lockedModel,
        title: 'Unlock this model',
        subtitle: 'To use this model, buy credits or upgrade to Plus.',
      });
      return false;
    }

    const emitOk = socketManager.emitSendUserMessage({
      userId,
      formattedMessage,
      conversationId: id,
    });
    if (emitOk === false) {
      // emitSendUserMessage already surfaced a toast and rolled back its
      // own optimistic state. Returning false tells ChatInput NOT to clear
      // the composer — the user's draft is preserved so they can retry.
      return false;
    }
    // Kill any in-flight cold-start restore so it can't drag us back to the
    // saved mid-conversation anchor, then proactively pin to the bottom the
    // instant the user sends — rather than waiting for the message to
    // round-trip through the socket + chat store. Without the abort, on long
    // chats the still-running restore re-pins to the middle right after we
    // scroll down ("thrown up high on send"). The store-driven pin (the
    // new-message effect) later reinforces this once the bubble arrives.
    abortScrollRestore();
    requestPinToBottomRef.current?.(true, 900, 5000);
    return true;
  }, [id, addToast, agent?.id, agent?.flags?.isPlusOnly, isPlus, isGhost, isHeldForReview, needsEmailVerification, needsPhoneVerification, user, userId, totalAvailableCredits, openModal, onFocus, abortScrollRestore, findLockedSelectedModel, isCoworkerChat, coworker.isActive, coworkerSend, coworkerEnqueue]);

  const handleSendNow = useCallback(async (formattedMessage) => {
    if (onFocus) onFocus();
    if (!isCoworkerChat) return handleSend(formattedMessage);
    try {
      abortScrollRestore();
      followLiveRef.current = false;
      workStreamPinnedIdRef.current = null;
      const ok = await coworkerSend(formattedMessage?.text, formattedMessage?.fileIds);
      return ok;
    } catch (err) {
      reportWorkLaunchError(err, { source: 'work_send_now', fallback: 'Failed to send' });
      return false;
    }
  }, [isCoworkerChat, handleSend, coworkerSend, abortScrollRestore, addToast, onFocus]);

  const handleQueuedFollowUpNow = useCallback(async (item) => {
    try {
      abortScrollRestore();
      followLiveRef.current = false;
      workStreamPinnedIdRef.current = null;
      const ok = await coworkerSendFollowUpNow(item);
      return ok;
    } catch (err) {
      reportWorkLaunchError(err, { source: 'work_send_now', fallback: 'Failed to send' });
      return false;
    }
  }, [coworkerSendFollowUpNow, abortScrollRestore, addToast]);

  const handleCancelInferencing = useCallback(async () => {
    // Coworker thread: "Stop" settles the task session (releases the credit
    // hold, revokes the session key) rather than cancelling an inference.
    if (isCoworkerChat) {
      try {
        await coworkerStop();
      } catch (err) {
        addToast(err?.message || 'Failed to stop', 'error');
      }
      return;
    }

    if (!(await socketManager.requireConnection())) {
      return;
    }

    socketManager.emitCancelUserConversationInference(userId, id);
  }, [id, userId, isCoworkerChat, coworkerStop, addToast]);

  const handleFollowUpSuggestionPress = useCallback(async (suggestionText) => {
    if (!suggestionText?.trim()) return;

    if (isHeldForReview) {
      openModal('AccountReviewHoldModal', { source: 'follow_up_suggestion' });
      return;
    }

    clearFollowUpSuggestions(id);

    // Work threads send through the task session, not the chat inference
    // socket — otherwise the suggestion would open a regular chat turn.
    // A pill click is a user message and must appear in the transcript
    // immediately (steer while live, new turn while idle).
    if (isCoworkerChat) {
      await handleSendNow({ text: suggestionText.trim(), fileIds: [] });
      return;
    }

    if (!(await socketManager.requireConnection())) {
      return;
    }

    if (!isGhost && needsPhoneVerification) {
      openModal('VerifyPhoneModal', { source: 'follow_up_suggestion' });
      return;
    }

    if (!isGhost && needsEmailVerification) {
      openModal('VerifyEmailModal', { source: 'follow_up_suggestion' });
      return;
    }

    if (!isPlus && !isGhost && !hasAvailableCredits(totalAvailableCredits)) {
      openCreditsRecoveryModal(openModal, user, {
        source: 'follow_up_suggestion',
        title: "Out of credits",
        subtitle: `You've used all your free credits. Upgrade to Plus for free default model messages and ${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits.`,
      });
      return;
    }

    // Carry over the user's currently selected model/agent for this conversation
    // so follow-up suggestions respect the active model selector (e.g. a third-party
    // model) instead of falling back to the default Arya model on the backend.
    const chatInputState = useChatInputStore.getState();
    const inputState = chatInputState.inputStates?.[id];
    const selectedModelIds = Array.isArray(inputState?.selectedModelIds)
      ? inputState.selectedModelIds
      : [];
    const selectedAgentIds = Array.isArray(inputState?.selectedAgentIds)
      ? inputState.selectedAgentIds
      : [];
    const filterValidIds = chatInputState.filterValidIds;
    const modelIds = typeof filterValidIds === 'function' ? filterValidIds(selectedModelIds) : selectedModelIds;

    const lockedModel = findLockedSelectedModel(modelIds);
    if (lockedModel) {
      openModelAccessPaywall(openModal, {
        model: lockedModel,
        title: 'Unlock this model',
        subtitle: 'To use this model, buy credits or upgrade to Plus.',
      });
      return;
    }

    const formattedMessage = {
      text: suggestionText.trim(),
      modelIds,
      agentIds: typeof filterValidIds === 'function' ? filterValidIds(selectedAgentIds) : selectedAgentIds,
      actions: [],
      fileIds: [],
      parameters: {},
    };

    socketManager.emitSendUserMessage({
      userId,
      formattedMessage,
      conversationId: id,
    });
    // Same as handleSend: don't let an in-flight cold-start restore yank the
    // viewport back to the saved anchor; head straight to the bottom.
    abortScrollRestore();
    requestPinToBottomRef.current?.(true, 900, 5000);
  }, [id, userId, isPlus, isGhost, isHeldForReview, needsEmailVerification, needsPhoneVerification, totalAvailableCredits, openModal, user, clearFollowUpSuggestions, abortScrollRestore, findLockedSelectedModel, isCoworkerChat, handleSendNow]);

  // Scroll-to-bottom button animation
  useEffect(() => {
    if (showScrollButton) {
      setIsAnimatingOut((prev) => (prev ? false : prev));
      Animated.spring(scrollButtonAnimation, {
        toValue: 1,
        useNativeDriver: true,
        tension: 80,
        friction: 9,
      }).start();
    } else {
      setIsAnimatingOut((prev) => (prev ? prev : true));
      Animated.spring(scrollButtonAnimation, {
        toValue: 0,
        useNativeDriver: true,
        tension: 80,
        friction: 9,
      }).start(() => {
        setIsAnimatingOut((prev) => (prev ? false : prev));
      });
    }
  }, [showScrollButton, scrollButtonAnimation]);

  // Skip list auto-pin while the composer is catching up to a keyboard move.
  const previousKeyboardHeightRef = useRef(keyboardHeight);
  useEffect(() => {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') return undefined;
    if (previousKeyboardHeightRef.current === keyboardHeight) return undefined;
    previousKeyboardHeightRef.current = keyboardHeight;
    keyboardAnimatingRef.current = true;
    const timeoutId = setTimeout(() => {
      keyboardAnimatingRef.current = false;
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [keyboardHeight]);

  // Compute banner count for current conversation context
  const selectBannerCount = useCallback(
    (state) => (state.inputStates?.[id]?.banners?.length) || 0,
    [id]
  );
  const bannerCount = useChatInputStore(selectBannerCount);
  const extraPaddingBottom = bannerCount > 0 ? bannerCount * 50 : 0;

  const fallbackListPaddingBottom = (
    effectiveChatInputStyle === CHAT_INPUT_STYLE.FLOATING
      ? (showDisclaimer ? 200 : 160)
      : (showDisclaimer ? 160 : 120)
  ) + extraPaddingBottom
    + (showSuggestionsBar ? 80 : 0)
    + ((isAuthenticated && isOwner) ? Math.max(keyboardHeight, insets.bottom) : 0);
  // Once the composer has mounted, its measured box already includes banners,
  // suggestions, keyboard lift, and safe-area padding. Prefer that single
  // source of truth so those features do not update list padding once from an
  // estimate and then again from ResizeObserver.
  // Work's follow-up queue sits in the composer overlay. Give the transcript
  // a little extra room so the live status (and a reply that lands after it)
  // does not tuck under that box.
  const workDockGap = isCoworkerChat ? 12 : 0;
  const listPaddingBottom = isDetailed
    ? 0
    : (Platform.OS === 'web' && isXS
      ? ((isAuthenticated && isOwner) && inputContainerHeight > 0
        ? inputContainerHeight + 8 + workDockGap
        : fallbackListPaddingBottom)
      : Math.max(
          fallbackListPaddingBottom,
          (isAuthenticated && isOwner) ? inputContainerHeight + workDockGap : 0
        ));

  // FlatList contentContainerStyle must stay referentially stable while the
  // user scrolls. Bundling it with scroll-chrome state (showScrollButton,
  // isNearTop, button animation) used to recreate this object on every
  // near-bottom / near-top threshold cross — on mobile Chrome that re-applied
  // contentContainerStyle mid-gesture and made the list jump while reading.
  const listContentContainerStyle = useMemo(() => ({
    paddingHorizontal: 0,
    paddingTop: isXS || isCompact ? 15 : 20,
    paddingBottom: listPaddingBottom,
    ...(Platform.OS === 'web' && {
      overscrollBehavior: 'contain',
      // Studio open remounts/reflows the column. Overflow-anchor plus JS
      // pins fight each other and jump the thread as tokens stream.
      overflowAnchor: isVideoStudioOpen ? 'none' : 'auto',
    }),
  }), [isXS, isCompact, isVideoStudioOpen, listPaddingBottom]);

  // Dynamic styles (chrome / chrome-adjacent — safe to churn during scroll)
  const styles = useMemo(() => ({
    ...baseStyles,
    container: {
      flex: 1,
      backgroundColor: colors.solid_color_primary,
      borderTopWidth: isNearTop && !isDetailed ? 0 : 1,
      borderTopColor: isNearTop && !isDetailed ? 'transparent' : colors.border_color_secondary,
    },
    mainContent: {
      flex: 0,
      position: 'absolute',
      ...(Platform.OS === 'web' && { minHeight: 0 }),
      top: 0,
      left: 0,
      right: 0,
      bottom: isAuthenticated ? 0 : 80,
      flexDirection: 'column',
      ...(Platform.OS === 'web' && { display: 'flex', transition: 'right 0.3s ease-in-out' }),
    },
    lockColumn: {
      width: '100%',
      paddingHorizontal: isXS || isCompact ? 12 : 20,
    },
    lockColumnInner: {
      width: '100%',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    list: listContentContainerStyle,
    inputContainer: {
      width: isXS ? 'auto' : '100%',
      ...(Platform.OS === 'web' && { boxSizing: 'border-box' }),
      paddingHorizontal: 0,
      alignItems: 'center',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      // Work keeps the guide and composer in this overlay. A glass
      // background lets the transcript paint through both, which reads as
      // the output sitting behind the dock. Regular chat still blurs.
      backgroundColor: (isXS || isCoworkerChat) ? colors.solid_color_primary : 'transparent',
      paddingBottom: isAuthenticated && isOwner
        ? (Platform.OS === 'web' ? keyboardHeight : Math.max(keyboardHeight, insets.bottom))
        : 0,
    },
    composerStack: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      zIndex: 11,
    },
    composerGate: {
      maxHeight: Platform.OS === 'web' ? '40vh' : 300,
      overflow: 'hidden',
      minHeight: 0,
      marginBottom: 8,
      width: '100%',
    },
    scrollToBottomButtonContainer: {
      position: 'absolute',
      top: -44,
      alignSelf: 'center',
      zIndex: 20,
      opacity: scrollButtonAnimation,
      transform: [{
        translateY: scrollButtonAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [20, 0],
        })
      }, {
        scale: scrollButtonAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [0.5, 1],
        })
      }],
      pointerEvents: (!showScrollButton && !isAnimatingOut) ? 'none' : 'auto',
    },
    scrollToBottomButton: {
      backgroundColor: themeStyles.backgroundColor,
      borderColor: themeStyles.borderColor,
      ...(Platform.OS === 'web' ? {
        backdropFilter: 'blur(18px) saturate(1.8)',
        WebkitBackdropFilter: 'blur(18px) saturate(1.8)',
      } : (
        Platform.OS === 'macos'
          ? {}
          : {
              shadowColor: themeStyles.shadowColor,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.15,
              shadowRadius: 8,
              elevation: 4,
            }
      )),
      borderWidth: 1,
    },
    scrollToBottomLiveText: {
      fontSize: 13,
      maxWidth: 260,
    },
    chatInputDetailedContainer: {
      zIndex: 10,
      height: '100%',
      backgroundColor: 'transparent',
    },
    archiveLoaderContainer: {
      width: '100%',
      alignItems: 'center',
      paddingTop: 2,
      paddingBottom: 12,
    },
    archiveLoaderButton: {
      backgroundColor: colors.solid_color_block,
      borderColor: colors.border_color_secondary,
      borderWidth: 1,
    },
  }), [isXS, isCompact, isCoworkerChat, colors, isAuthenticated, scrollButtonAnimation, showScrollButton, isAnimatingOut, themeStyles.backgroundColor, themeStyles.borderColor, themeStyles.shadowColor, keyboardHeight, isOwner, isNearTop, isDetailed, insets.bottom, listContentContainerStyle]);

  // Socket initialization / mark as read
  useEffect(() => {
    if (isAuthenticated) {
      if (!isCompact) {
        setCurrentLibraryId(id);
      }

      // Only the owner can mark a conversation read — shared/public chats
      // viewed by other users 404 on the /read endpoint and spam the console.
      if (isOwner) {
        useChatStore.getState().markChatAsRead(id);
      }

      return () => {
        if (!isCompact) {
          setCurrentLibraryId(null);
        }
      };
    }
  }, [id, isAuthenticated, isCompact, isOwner]);

  const pinStabilizeMsRef = useRef(0);

  const clearBottomRepins = useCallback(() => {
    const handles = bottomRepinHandlesRef.current;
    bottomRepinHandlesRef.current = [];
    for (const h of handles) {
      if (h?.type === 'raf') {
        if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(h.id);
      } else {
        clearTimeout(h.id);
      }
    }
  }, []);

  const clearBottomPin = useCallback(() => {
    wantsBottomRef.current = false;
    pinStabilizeMsRef.current = 0;
    if (bottomStabilizeTimerRef.current) {
      clearTimeout(bottomStabilizeTimerRef.current);
      bottomStabilizeTimerRef.current = null;
    }
    if (bottomMaxTimerRef.current) {
      clearTimeout(bottomMaxTimerRef.current);
      bottomMaxTimerRef.current = null;
    }
    clearBottomRepins();
  }, [clearBottomRepins]);

  // Native drag callbacks do not reliably fire for Firefox wheel/touchpad input
  // (or Android Chrome touch scrolling through RN Web's FlatList). Cancel an
  // active programmatic pin at the input event — in capture phase so we win
  // even when the scrollable child stops bubbling — before a scheduled re-pin
  // can erase the user's smaller incremental scroll deltas.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const node = messagesContainerRef.current;
    if (!node?.addEventListener) return undefined;

    const handleDirectScrollIntent = (event) => {
      // onScrollBeginDrag doesn't fire for web wheel/trackpad input, so this
      // is the only signal that marks genuine user scroll intent on web.
      hasUserDraggedRef.current = true;
      userScrollIntentUntilRef.current = Date.now() + 1000;
      if (event?.type === 'wheel' && event.deltaY < 0) {
        followLiveRef.current = false;
      }
      if (event?.type === 'wheel' && event.deltaY > 0) {
        lastDownwardScrollAtRef.current = Date.now();
      }
      if (wantsBottomRef.current) clearBottomPin();
    };
    const handleWheelOrTouchScrollIntent = (event) => {
      handleDirectScrollIntent(event);
      abortScrollRestore();
      // The veiled list has pointer-events off, so this container listener is
      // the only signal that the user wants to scroll during positioning.
      // Reveal rather than making them wait out the settle.
      endListSettleVeilRef.current?.();
    };
    // A touch scroll on mobile web can run well past the 1s intent window
    // (a long drag, then momentum). Keep the window fresh while the finger
    // moves and give momentum a little longer after it lifts, so scrolling
    // back down to the tail still re-arms follow-live.
    const handleTouchMoveIntent = () => {
      userScrollIntentUntilRef.current = Date.now() + 1000;
    };
    const handleTouchEndIntent = () => {
      userScrollIntentUntilRef.current = Date.now() + 1500;
    };
    const opts = { passive: true, capture: true };
    node.addEventListener('wheel', handleWheelOrTouchScrollIntent, opts);
    node.addEventListener('touchstart', handleWheelOrTouchScrollIntent, opts);
    node.addEventListener('touchmove', handleTouchMoveIntent, opts);
    node.addEventListener('touchend', handleTouchEndIntent, opts);
    // pointerdown cancels an active bottom-pin (clicking the list means the
    // user took over) but must NOT abort an explicit jump-to-message. Closing
    // search lands mouseup on the chat underneath and would otherwise drop
    // the pending scroll before ChatView can honor it.
    node.addEventListener('pointerdown', handleDirectScrollIntent, opts);

    return () => {
      node.removeEventListener('wheel', handleWheelOrTouchScrollIntent, opts);
      node.removeEventListener('touchstart', handleWheelOrTouchScrollIntent, opts);
      node.removeEventListener('touchmove', handleTouchMoveIntent, opts);
      node.removeEventListener('touchend', handleTouchEndIntent, opts);
      node.removeEventListener('pointerdown', handleDirectScrollIntent, opts);
    };
  }, [clearBottomPin, abortScrollRestore, isLoading, error]);

  const restoreStudioScrollIfSnapped = useCallback((nextOffset, el) => {
    if (!isVideoStudioOpenRef.current) return false;
    const previousOffset = scrollOffsetRef.current;
    if (!shouldRestoreStudioScroll({
      previousOffset,
      nextOffset,
      userScrolling: isUserScrollingRef.current,
      userIntentUntil: userScrollIntentUntilRef.current,
      lastDownwardAt: lastDownwardScrollAtRef.current,
      pinningToBottom: wantsBottomRef.current,
    })) return false;
    lastProgrammaticPinAtRef.current = Date.now();
    if (el) el.scrollTop = previousOffset;
    try {
      flatListRef.current?.scrollToOffset?.({ offset: previousOffset, animated: false });
    } catch (_) { /* list may be mid-remount */ }
    return true;
  }, []);

  const pinToBottomNow = useCallback((animated = false) => {
    if (shouldHoldChatScroll({ ...chatScrollStateRef.current, followLive: followLiveRef.current })) {
      clearBottomPin();
      return;
    }
    lastProgrammaticPinAtRef.current = Date.now();
    if (Platform.OS === 'web') {
      const el = getScrollElement();
      if (el) {
        if (animated) {
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        } else {
          el.scrollTop = el.scrollHeight;
        }
        return;
      }
    }
    if (!flatListRef.current) return;
    try {
      if (flatListRef.current.scrollToOffset) {
        flatListRef.current.scrollToOffset({ offset: 999999, animated });
      } else if (flatListRef.current.scrollToEnd) {
        flatListRef.current.scrollToEnd({ animated });
      }
    } catch (error) {
      console.warn('Failed to pin to bottom:', error);
    }
  }, [getScrollElement, clearBottomPin]);

  // Put a Work turn at the top of the list so the reply can grow downward
  // in place instead of the tail-pin shoving the reader down the page.
  const pinMessageToStartNow = useCallback((messageId) => {
    if (!messageId) return;
    if (isVideoStudioOpenRef.current) return;
    lastProgrammaticPinAtRef.current = Date.now();
    if (Platform.OS === 'web') {
      const scroller = getScrollElement();
      if (scrollMessageNodeToStart(messageId, scroller)) return;
    }
    const index = findGroupedMessageIndex(groupedMessagesRef.current || [], messageId);
    if (index < 0 || !flatListRef.current?.scrollToIndex) return;
    try {
      const listIndex = index;
      flatListRef.current.scrollToIndex({ index: listIndex, animated: false, viewPosition: 0 });
    } catch (_) {}
  }, [getScrollElement]);

  const startRepinHandlesRef = useRef([]);
  const clearStartRepins = useCallback(() => {
    const handles = startRepinHandlesRef.current;
    startRepinHandlesRef.current = [];
    for (const h of handles) {
      if (h?.type === 'raf') {
        if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(h.id);
      } else {
        clearTimeout(h.id);
      }
    }
  }, []);
  const scheduleStartRepins = useCallback((messageId) => {
    if (isVideoStudioOpenRef.current) return;
    clearStartRepins();
    const fire = () => pinMessageToStartNow(messageId);
    fire();
    if (typeof requestAnimationFrame === 'function') {
      startRepinHandlesRef.current.push({ type: 'raf', id: requestAnimationFrame(fire) });
    }
    for (const delay of [50, 150, 300]) {
      startRepinHandlesRef.current.push({ type: 'timeout', id: setTimeout(fire, delay) });
    }
  }, [clearStartRepins, pinMessageToStartNow]);
  useEffect(() => () => clearStartRepins(), [clearStartRepins]);

  // Re-assert the bottom across the next few frames so the pin survives the
  // async MVC-removal render and lands after each layout pass. Each attempt is
  // a no-op once the user takes over (wantsBottomRef cleared) or the list is
  // gone. Cheap: a handful of scrollToOffset calls over ~600ms.
  const scheduleBottomRepins = useCallback(() => {
    clearBottomRepins();
    const fire = () => {
      if (!wantsBottomRef.current || !flatListRef.current) return;
      pinToBottomNow(false);
    };
    if (typeof requestAnimationFrame === 'function') {
      bottomRepinHandlesRef.current.push({ type: 'raf', id: requestAnimationFrame(fire) });
    }
    for (const delay of [50, 150, 300, 600]) {
      bottomRepinHandlesRef.current.push({ type: 'timeout', id: setTimeout(fire, delay) });
    }
  }, [clearBottomRepins, pinToBottomNow]);

  // Restart the debounced stabilize timer. Called from onContentSizeChange so the
  // pin stays active as long as content keeps growing, and releases shortly after
  // layout quiets down.
  const bumpStabilizeTimer = useCallback(() => {
    if (!wantsBottomRef.current) return;
    const ms = pinStabilizeMsRef.current;
    if (!ms) return;
    if (bottomStabilizeTimerRef.current) clearTimeout(bottomStabilizeTimerRef.current);
    bottomStabilizeTimerRef.current = setTimeout(() => {
      wantsBottomRef.current = false;
      bottomStabilizeTimerRef.current = null;
    }, ms);
  }, []);

  // Request the list to stay pinned at the bottom through any pending content-size
  // growth. The pin is released when content has been quiet for `stabilizeMs`
  // (debounced — resets on every content-size change), after `maxMs` as a hard
  // cap, or immediately on user interaction.
  const requestPinToBottom = useCallback((animated = false, stabilizeMs = 600, maxMs = 4000, { followLive = !mobileReadingChat } = {}) => {
    if (followLive || mobileReadingChat) followLiveRef.current = followLive;
    wantsBottomRef.current = true;
    pinStabilizeMsRef.current = stabilizeMs;
    // A bottom-pin and a restore anchor-pin are mutually exclusive — if both
    // are active, onContentSizeChange's `wantsBottomRef`-first check keeps the
    // bottom-pin winning, but residual anchor scrollToIndex corrections can
    // still fight the bottom-pin's scrollToOffset,
    // producing the "jumps to the middle on send" symptom on long chats that
    // were cold-restored to a saved anchor. Clear the anchor up front so the
    // bottom-pin owns the viewport cleanly.
    anchorPinRef.current = null;
    pinToBottomNow(animated);
    // Defeat the iOS MVC race + cover the pre-message-arrival window by
    // re-firing the scroll across the next few frames.
    scheduleBottomRepins();
    if (bottomStabilizeTimerRef.current) clearTimeout(bottomStabilizeTimerRef.current);
    if (bottomMaxTimerRef.current) clearTimeout(bottomMaxTimerRef.current);
    bottomStabilizeTimerRef.current = setTimeout(() => {
      wantsBottomRef.current = false;
      bottomStabilizeTimerRef.current = null;
    }, stabilizeMs);
    bottomMaxTimerRef.current = setTimeout(() => {
      wantsBottomRef.current = false;
      bottomMaxTimerRef.current = null;
    }, maxMs);
  }, [pinToBottomNow, scheduleBottomRepins, mobileReadingChat]);
  // Expose the latest requestPinToBottom to earlier-defined handlers (handleSend)
  // via a stable ref, sidestepping the const temporal-dead-zone.
  requestPinToBottomRef.current = requestPinToBottom;

  // Reveal the list at its (now stable) position.
  const endListSettleVeil = useCallback(() => {
    if (settleVeilQuietTimerRef.current) {
      clearTimeout(settleVeilQuietTimerRef.current);
      settleVeilQuietTimerRef.current = null;
    }
    if (settleVeilMaxTimerRef.current) {
      clearTimeout(settleVeilMaxTimerRef.current);
      settleVeilMaxTimerRef.current = null;
    }
    if (!settleVeilActiveRef.current) return;
    settleVeilActiveRef.current = false;
    Animated.timing(listSettleOpacity, {
      toValue: 1,
      duration: 220,
      useNativeDriver: true,
    }).start(() => {
      // Unmount the skeleton only after the cross-fade completes.
      setIsSettleVeilUp(false);
    });
  }, [listSettleOpacity]);
  useEffect(() => {
    endListSettleVeilRef.current = endListSettleVeil;
  }, [endListSettleVeil]);

  // Debounced "content has gone quiet" detector; restarted on every
  // content-size change while the veil is up.
  const bumpListSettleVeil = useCallback(() => {
    if (!settleVeilActiveRef.current) return;
    if (settleVeilQuietTimerRef.current) clearTimeout(settleVeilQuietTimerRef.current);
    settleVeilQuietTimerRef.current = setTimeout(() => {
      // Live runs hold the veil until the first event snapshot so tool
      // trails do not pop in after reveal. Idle history already has the
      // conversation — do not wait on the orchestrator.
      if (settleVeilActiveRef.current && !workEventsReadyRef.current) {
        bumpListSettleVeil();
        return;
      }
      endListSettleVeil();
    }, 280);
  }, [endListSettleVeil]);

  const beginListSettleVeil = useCallback((maxMs = 2200, options) => {
    // Regular web chats settle without the native measurement shake. Work
    // threads do not: they render from the top, then grow as tool trails and
    // the event snapshot land. Veil those so the user only sees the tail.
    if (Platform.OS === 'web' && !options?.allowWeb) return;
    settleVeilActiveRef.current = true;
    listSettleOpacity.setValue(0);
    setIsSettleVeilUp(true);
    // Safety: if no content-size events arrive at all (e.g. tiny chat already
    // measured), reveal quickly rather than waiting for the hard cap. Work
    // still waits on the event snapshot via bumpListSettleVeil.
    bumpListSettleVeil();
    if (settleVeilMaxTimerRef.current) clearTimeout(settleVeilMaxTimerRef.current);
    settleVeilMaxTimerRef.current = setTimeout(endListSettleVeil, maxMs);
  }, [bumpListSettleVeil, endListSettleVeil, listSettleOpacity]);

  // Clear timers on unmount
  useEffect(() => () => {
    clearBottomPin();
    if (settleVeilQuietTimerRef.current) clearTimeout(settleVeilQuietTimerRef.current);
    if (settleVeilMaxTimerRef.current) clearTimeout(settleVeilMaxTimerRef.current);
    if (showScrollButtonRafRef.current && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(showScrollButtonRafRef.current);
      showScrollButtonRafRef.current = 0;
    }
  }, [clearBottomPin]);

  // Reset the per-conversation initial-scroll latch when the chat changes.
  useEffect(() => {
    initialScrolledIdRef.current = null;
    lastMessageId.current = null;
    studioUserTurnRef.current = null;
    prependCompensationRef.current = null;
    anchorPinRef.current = null;
    hasUserDraggedRef.current = false;
    userScrollIntentUntilRef.current = 0;
    followLiveRef.current = false;
    workStreamPinnedIdRef.current = null;
    clearBottomPin();
    clearStartRepins();
    // Drop any settle veil left over from the previous conversation —
    // otherwise switching from a long chat (veil up, list at opacity 0) to a
    // short one (<15 messages, which never starts a new veil) leaves the new
    // chat invisible until the previous chat's stale timers fire. Reset
    // instantly rather than fading: a fresh veil is started right after for
    // long chats by the initial-load effect below.
    if (settleVeilQuietTimerRef.current) {
      clearTimeout(settleVeilQuietTimerRef.current);
      settleVeilQuietTimerRef.current = null;
    }
    if (settleVeilMaxTimerRef.current) {
      clearTimeout(settleVeilMaxTimerRef.current);
      settleVeilMaxTimerRef.current = null;
    }
    if (settleVeilActiveRef.current) {
      settleVeilActiveRef.current = false;
      listSettleOpacity.stopAnimation();
      listSettleOpacity.setValue(1);
      setIsSettleVeilUp(false);
    }
  }, [id, clearBottomPin, clearStartRepins, listSettleOpacity]);

  // Scroll to bottom on initial load (skip if a scroll-to-message request is pending for this chat).
  // Fires once per conversation id so new incoming messages don't yank an
  // attentive reader back to the bottom.
  useEffect(() => {
    if (!chat?.messages?.length) return;
    if (initialScrolledIdRef.current === id) return;
    const pending = useUIStore.getState().pendingScrollRequest;
    if (pending && pending.conversationId === id) {
      initialScrolledIdRef.current = id;
      return;
    }
    // If the conversation is mid-inference the first time we encounter it,
    // latch WITHOUT pinning. A brand-new chat is navigated into with
    // isInferencing already set (see app.jsx), so the only initial-load pin
    // that could fire here is a *deferred* one — it would run the instant
    // generation finishes and yank the user to the very bottom right as they
    // begin reading the first response. Latching neutralizes that. Existing
    // chats are opened while idle and fall through to the normal pin below.
    // Work threads are the opposite: the live tail *is* the thing to watch,
    // and isInferencing stays true for the whole run.
    if ((isInferencing || isInferencingWaiting) && !isCoworkerChat) {
      initialScrolledIdRef.current = id;
      return;
    }
    initialScrolledIdRef.current = id;
    // Hide the settling: position the list while it's veiled and fade in once
    // layout is quiet, so the user never sees the measurement shake. Short
    // chats measure in a frame or two and don't shake — skip the veil so they
    // keep appearing instantly. Only live Work threads need to wait for
    // tool trails; settled history should paint immediately from cache.
    const messageCount = chat?.messages?.length || 0;
    if (isCoworkerChat && coworker.isActive && messageCount >= 6) {
      // Short hard cap: a live run's transcript never goes quiet, and holding
      // a skeleton over streaming work is worse than showing it settle.
      beginListSettleVeil(1600, { allowWeb: true });
    } else if (!isCoworkerChat && messageCount >= 15) {
      beginListSettleVeil();
    }
    // Use an extra-long pin window for initial load because long chats may take
    // time to finish rendering markdown, code, math, and images, all of which
    // grow the content height asynchronously after first paint.
    // Live Work replies stream in place from their start; a long tail-pin
    // here is what shoved a 10k-px essay past the reader. A short pin without
    // follow-live still lands tool-only runs on the live status.
    if (isCoworkerChat && coworker.isActive) {
      requestPinToBottom(false, 400, 1200, { followLive: false });
    } else {
      requestPinToBottom(false, isCoworkerChat ? 1000 : 800, isCoworkerChat ? 8000 : 6000);
    }
  }, [id, chat?.messages?.length, isInferencing, isInferencingWaiting, isCoworkerChat, coworker.isActive, requestPinToBottom, beginListSettleVeil]);

  // Event snapshot can land after the last content-size change. Nudge the
  // veil so it can reveal once trails have attached.
  useEffect(() => {
    if (!isCoworkerChat) return;
    if (!coworkerHasSnapshot && !(coworker.events?.length > 0)) return;
    bumpListSettleVeil();
  }, [isCoworkerChat, coworkerHasSnapshot, coworker.events?.length, bumpListSettleVeil]);

  // Auto-scroll during voice mode
  useEffect(() => {
    if (!isVoiceModeActive || !flatListRef.current || !chat?.messages?.length) return;
    if (keyboardAnimatingRef.current) return;
    requestPinToBottom(true, 500, 2500);
  }, [isVoiceModeActive, chat?.messages?.length, requestPinToBottom]);

  // Auto-scroll when follow-up suggestions appear
  useEffect(() => {
    if (mobileReadingChat) return;
    if (!flatListRef.current) return;
    if (isInferencing || isInferencingWaiting) return;

    const hasFollowUps = followUpSuggestions?.suggestions?.length > 0;

    if (hasFollowUps) {
      const suggestionScrollTimer = setTimeout(() => {
        if (!flatListRef.current) return;
        // Once the user has taken control of the viewport, follow-up chrome
        // must not start a new bottom pin even if they happen to be close to
        // the end of the answer.
        if (Platform.OS === 'web' && isXS && hasUserDraggedRef.current) return;
        const layoutH = listLayoutHeightRef.current;
        const contentH = contentHeightRef.current;
        const offset = scrollOffsetRef.current;
        // Tight threshold: a loose 100px window was re-pinning users who had
        // already scrolled up a bit to read the answer when follow-ups landed.
        const actuallyAtBottom = layoutH > 0 && contentH > 0
          ? offset + layoutH >= contentH - 24
          : isAtBottomRef.current;

        if (actuallyAtBottom) {
          requestPinToBottom(true, 500, 2000);
        }
      }, 300);
      return () => clearTimeout(suggestionScrollTimer);
    }
  }, [followUpSuggestions?.suggestions?.length, isInferencing, isInferencingWaiting, requestPinToBottom, isXS, mobileReadingChat]);

  // Debug logging
  useEffect(() => {
    if (__DEV__) {
      console.log('[ChatView] Chat data updated:', {
        chatId: chat?.id,
        messageCount: chat?.messages?.length,
        chatExists: !!chat,
      });
    }
  }, [chat, chat?.messages?.length]);

  // Memoize chat data. Streaming flushes change only message content — never
  // order — so cache the sort by a structure signature (message keys + hidden
  // flags) and re-run the O(n log n) sort + date parses only when the
  // structure actually changes. Between structure changes the previous
  // ordering is replayed with the fresh message objects.
  const chatSortCacheRef = useRef({ signature: null, visibleKeys: [] });
  // Work threads render the projected transcript (server rows + live tail);
  // the chat store only holds the server projection for them.
  const workTranscript = coworker.transcript;
  const sortCacheConversationIdRef = useRef(id);
  // Native Drawer reuses this ChatView instance across /c/:id params.
  // Reset during render (not in an effect) so this pass cannot replay the
  // previous conversation's keys.
  if (sortCacheConversationIdRef.current !== id) {
    sortCacheConversationIdRef.current = id;
    chatSortCacheRef.current = { signature: null, visibleKeys: [] };
  }
  const chatData = useMemo(() => {
    const isWork = isCoworkerChat && workTranscript;
    const source = isWork ? workTranscript : (chat?.messages || []);
    if (isWork) {
      // Work transcript rows are already projected in exact chronological and
      // sequence order (server messages, event tail, live draft, pending sends,
      // queued follow-ups). Do NOT re-sort by timestamp — clock skew or
      // synthetic timestamps would scramble the message flow.
      const visible = [];
      let signature = '';
      for (let i = 0; i < source.length; i++) {
        const m = source[i];
        if (m.isHidden) continue;
        visible.push(m);
        signature += (m._stableKey || m.id || m._id || `idx-${i}`) + '|';
      }
      return {
        messages: visible,
        title: chatTitle,
        isArchived: chat?.isArchived,
        sortSignature: signature,
        visibleKeys: visible.map(m => m._stableKey || m.id || m._id),
      };
    }

    const keys = new Array(source.length);
    let signature = '';
    for (let i = 0; i < source.length; i++) {
      const m = source[i];
      const key = m._stableKey || m.id || m._id || `idx-${i}`;
      keys[i] = key;
      signature += key + (m.isHidden ? '1' : '0') + '|';
    }

    let sorted;
    let visibleKeys;
    const cache = chatSortCacheRef.current;
    let usedCache = false;
    if (cache.signature === signature) {
      const byKey = new Map();
      for (let i = 0; i < source.length; i++) byKey.set(keys[i], source[i]);
      const replayed = new Array(cache.visibleKeys.length);
      let cacheHit = true;
      for (let i = 0; i < cache.visibleKeys.length; i++) {
        const msg = byKey.get(cache.visibleKeys[i]);
        if (!msg) {
          cacheHit = false;
          break;
        }
        replayed[i] = msg;
      }
      if (cacheHit) {
        sorted = replayed;
        visibleKeys = cache.visibleKeys;
        usedCache = true;
      }
    }
    if (!usedCache) {
      const decorated = [];
      for (let i = 0; i < source.length; i++) {
        const m = source[i];
        if (m.isHidden) continue;
        decorated.push([new Date(m.timestamp || m.date || 0).getTime(), m, keys[i]]);
      }
      // Array.prototype.sort is stable, so ties keep their original order —
      // identical behavior to the previous comparator-based sort.
      decorated.sort((a, b) => a[0] - b[0]);
      sorted = decorated.map(entry => entry[1]);
      visibleKeys = decorated.map(entry => entry[2]);
    }

    return {
      messages: sorted,
      title: chatTitle,
      isArchived: chat?.isArchived,
      sortSignature: signature,
      visibleKeys,
    };
  }, [chat?.messages, isCoworkerChat, workTranscript, chatTitle, chat?.isArchived]);

  // Layout callbacks and scheduled pins must see the response in the same
  // render, before effects run, so the first token cannot move the viewport.
  chatScrollStateRef.current = {
    mobileReadingChat,
    isInferencing: !!(isInferencing || isInferencingWaiting),
    hasAssistantResponse: chatData.messages[chatData.messages.length - 1]?.role === 'assistant',
  };

  // Persist the sort cache after commit — mutating a ref during render would
  // leak work from renders React discards.
  useEffect(() => {
    chatSortCacheRef.current = {
      signature: chatData.sortSignature,
      visibleKeys: chatData.visibleKeys,
    };
  }, [chatData]);

  // Sending should always reveal the user's own bubble, even in very long
  // conversations where FlatList/content measurement settles over multiple
  // frames.
  useEffect(() => {
    const latestMessage = chatData.messages[chatData.messages.length - 1];
    const latestMessageKey = latestMessage?._stableKey || latestMessage?.id || latestMessage?._id;
    if (!latestMessageKey) return;

    const previousMessageKey = lastMessageId.current;
    lastMessageId.current = latestMessageKey;

    if (!previousMessageKey) return;
    if (latestMessageKey === previousMessageKey) return;

    // Work retargets `cw-<task>-streaming` → `cw-<task>-<seq>` mid-reveal.
    // Treating that as a new bubble re-pins and yanks a reader who scrolled
    // up. Only a new user turn or an already-followed live tail should pin.
    // Work user turns pin to the *start* of the turn so the reply grows
    // downward like chat, instead of gluing the viewport to the tail.
    if (latestMessage?.role === 'user') {
      if (isCoworkerChat) {
        // Studio turns are handled below, including snapshots that contain
        // both the new user message and the first assistant response.
        if (isVideoStudioOpenRef.current) return;
        followLiveRef.current = false;
        // Mark the turn aligned so the streaming-reply start pin does not
        // steal the viewport from the prompt. The reply grows underneath.
        workStreamPinnedIdRef.current = latestMessageKey;
        clearBottomPin();
        // The video studio remounts the chat column; JS start-pins fight
        // overflow-anchor and jump the thread. Let the list stay put.
        if (!isVideoStudioOpenRef.current) scheduleStartRepins(latestMessageKey);
      } else {
        requestPinToBottom(true, 900, 5000);
      }
    } else if (mobileReadingChat && !followLiveRef.current) {
      // Also release the send pin for replies that arrive already complete.
      clearBottomPin();
    } else if (followLiveRef.current && isAtBottomRef.current) {
      requestPinToBottom(true, 900, 5000, { followLive: true });
    }
  }, [chatData.messages, requestPinToBottom, isCoworkerChat, clearBottomPin, scheduleStartRepins, mobileReadingChat]);

  useEffect(() => {
    if (!isCoworkerChat) return;
    let latestUser;
    for (let i = chatData.messages.length - 1; i >= 0; i -= 1) {
      if (chatData.messages[i].role === 'user') {
        latestUser = chatData.messages[i];
        break;
      }
    }
    const key = latestUser?._stableKey || latestUser?.id || latestUser?._id;
    if (!key) return;
    const previous = studioUserTurnRef.current;
    studioUserTurnRef.current = key;
    if (!previous || previous === key || !isVideoStudioOpenRef.current) return;
    // Redo / lock-take actions bypass the composer. Release any saved-history
    // restore and reveal the submitted turn after it actually enters the
    // transcript. Keep following layout and tool updates until the user scrolls.
    abortScrollRestore();
    clearStartRepins();
    workStreamPinnedIdRef.current = key;
    requestPinToBottom(false, 900, 5000);
  }, [chatData.messages, isCoworkerChat, abortScrollRestore, clearStartRepins, requestPinToBottom]);

  // Work threads keep their run timer and each tool step in the transcript
  // rather than in a panel above the composer. Both need the same centered
  // column the bubbles use.
  const workColumnStyle = useMemo(() => ({
    width: '100%',
    maxWidth: MESSAGE_WIDTH_AMOUNTS[messageWidth?.toUpperCase() || 'NARROW'] || MESSAGE_WIDTH_AMOUNTS.NARROW,
    alignSelf: 'center',
    paddingHorizontal: 16,
  }), [messageWidth]);
  const workTurns = useMemo(
    () => (isCoworkerChat ? buildWorkTurns(coworker.events) : null),
    [isCoworkerChat, coworker.events],
  );
  const workMessageKey = useMemo(
    () => (isCoworkerChat ? workMessageProjectionKey(chatData.messages) : ''),
    [isCoworkerChat, chatData.messages],
  );
  const workTaskCredits = Number(coworker.task?.creditsUsed)
    || Number(coworker.task?.budget?.creditsUsed)
    || 0;
  const workRuns = useMemo(
    () => (isCoworkerChat
      ? buildWorkRuns(chatData.messages, coworker.events, {
        isActive: coworker.isActive,
        organizerModel: coworker.task?.model,
        creditsUsed: workTaskCredits,
        turns: workTurns,
      })
      : null),
    // workMessageKey, not chatData.messages: streaming only changes content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCoworkerChat, workMessageKey, workTurns, coworker.isActive, coworker.task?.model, workTaskCredits],
  );
  const workLiveValue = useWorkLiveValue({
    taskError: coworker.task?.error,
    taskId: coworker.taskId,
    taskStatus: coworker.taskStatus,
    isActive: coworker.isActive,
    activityStatus: coworker.activityStatus,
    activityIcon: coworker.activityIcon,
    activityStartedAt: coworker.activityStartedAt,
    lastEventAt: coworker.lastEventAt,
    browserState: coworker.browserState,
    // While a settled follow-up/resume request is being accepted, the old
    // event log still ends at the previous session. Use the optimistic start
    // so the live clock never counts the paused time as active work.
    liveStartTs: coworker.optimisticLiveStartedAt ?? workRuns?.live?.startTs ?? null,
  });

  // Scrolled up to read history while Work is still going: the scroll-to-
  // bottom button becomes a "jump to live" pill carrying the current
  // activity, so the reader knows the run is alive without leaving the spot.
  const workJumpToLiveLabel = useMemo(() => {
    if (!isCoworkerChat || !coworker.isActive) return null;
    const messages = chatData.messages || [];
    let lastAssistantText = '';
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'assistant') {
        lastAssistantText = messages[i].content || '';
        break;
      }
    }
    if (!shouldShowWorkLiveActivity({
      isActive: true,
      events: coworker.events,
      lastAssistantText,
    })) return null;
    const status = String(coworker.activityStatus || WORK_THINKING_STATUS).trim();
    if (!status) return null;
    return status.length > 44 ? `${status.slice(0, 43).trimEnd()}…` : status;
  }, [isCoworkerChat, coworker.isActive, coworker.activityStatus, coworker.events, chatData.messages]);

  // Token batches often grow a row without a new message id, so the "new
  // bubble" pin never fires. Mobile chat follows only after a down-arrow tap;
  // other chat clients follow until the user scrolls up. Work does that only
  // for tool activity (no text yet) or when
  // the user taps jump-to-live; a streaming reply pins to its *start* once
  // and grows downward so a long essay does not shove the reader down the page.
  const liveTailLen = useMemo(() => {
    const msgs = chatData.messages || [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (m && (m.isStreaming || m.isTyping || m.isThinking)) {
        return String(m.content || '').length + String(m.thinkingContent || '').length;
      }
    }
    return 0;
  }, [chatData.messages]);
  const streamingWork = useMemo(
    () => (isCoworkerChat ? findStreamingWorkAssistant(chatData.messages) : null),
    [isCoworkerChat, chatData.messages],
  );
  streamingWorkRef.current = streamingWork;
  const pinLiveRafRef = useRef(0);
  useEffect(() => {
    if (anchorPinRef.current) return;
    const followLive = followLiveRef.current;
    const mode = getWorkStreamScrollMode({
      isCoworkerChat,
      hasStreamingText: !!streamingWork?.id,
      followLive,
    });
    if (mode === WORK_STREAM_SCROLL.START) {
      if (isVideoStudioOpenRef.current) return undefined;
      if (workStreamPinnedIdRef.current) return undefined;
      if (pinLiveRafRef.current) return undefined;
      pinLiveRafRef.current = requestAnimationFrame(() => {
        pinLiveRafRef.current = 0;
        if (anchorPinRef.current) return;
        if (followLiveRef.current) return;
        if (workStreamPinnedIdRef.current) return;
        const readingHistory = hasUserDraggedRef.current && !isAtBottomRef.current;
        workStreamPinnedIdRef.current = streamingWork.id || true;
        if (readingHistory) return;
        clearBottomPin();
        scheduleStartRepins(streamingWork.id);
      });
      return () => {
        if (pinLiveRafRef.current) {
          cancelAnimationFrame(pinLiveRafRef.current);
          pinLiveRafRef.current = 0;
        }
      };
    }
    if (!followLive) return undefined;
    if (pinLiveRafRef.current) return undefined;
    pinLiveRafRef.current = requestAnimationFrame(() => {
      pinLiveRafRef.current = 0;
      if (!followLiveRef.current || anchorPinRef.current) return;
      pinToBottomNow(false);
    });
    return () => {
      if (pinLiveRafRef.current) {
        cancelAnimationFrame(pinLiveRafRef.current);
        pinLiveRafRef.current = 0;
      }
    };
  }, [liveTailLen, streamingWork?.id, streamingWork?.length, coworker.events?.length, coworker.activityStatus, chatData.messages?.length, isCoworkerChat, pinToBottomNow, scheduleStartRepins, clearBottomPin]);

  // Empty streaming drafts stay out of the list; every other Work reply stays
  // in the transcript with its tool steps underneath.
  const visibleMessages = useMemo(() => {
    const hidden = workRuns?.hiddenMessageIds;
    const messages = chatData.messages || [];
    if (!hidden?.size) return messages;
    return messages.filter((message) => !hidden.has(message.id));
  }, [chatData.messages, workRuns]);

  // Group consecutive assistant messages. Group objects are reused from the
  // previous pass when their member messages are the same references, so a
  // streaming flush (which only replaces the one message being streamed)
  // does not hand every MessageGroup in a long thread a fresh props object
  // and force it to re-render.
  const groupCacheRef = useRef(new Map());
  const groupedResult = useMemo(() => {
    const rawMessages = visibleMessages;
    if (!rawMessages || rawMessages.length === 0) {
      return { items: [], nextGroups: new Map() };
    }

    const grouped = [];
    const previousGroups = groupCacheRef.current;
    const nextGroups = new Map();

    rawMessages.forEach((msg) => {
      const isAssistant = msg.role === 'assistant';
      const hasParent = msg.parentMessageId && msg.parentMessageId !== null;
      // Work replies carry their own trails and live status. Folding them
      // into MessageGroup skipped that chrome and left a finished bubble
      // as the last word while the run was still going.
      const isGroupable = isAssistant && hasParent && !isCoworkerChat;

      const lastItem = grouped[grouped.length - 1];

      if (isGroupable && lastItem && lastItem.type === 'group' && lastItem.parentMessageId === msg.parentMessageId) {
        lastItem.messages.push(msg);
      } else if (isGroupable) {
        grouped.push({
          type: 'group',
          id: `group-${msg.parentMessageId}`,
          parentMessageId: msg.parentMessageId,
          messages: [msg],
          role: 'assistant',
        });
      } else {
        grouped.push(msg);
      }
    });

    for (let i = 0; i < grouped.length; i++) {
      const item = grouped[i];
      if (item.type !== 'group') continue;
      const previous = previousGroups.get(item.id);
      if (
        previous
        && previous.messages.length === item.messages.length
        && previous.messages.every((m, idx) => m === item.messages[idx])
      ) {
        grouped[i] = previous;
        nextGroups.set(item.id, previous);
      } else {
        nextGroups.set(item.id, item);
      }
    }
    // Insert a date separator before the first item of each calendar day.
    // Separators are derived from the item timestamps, so their ids stay
    // stable across streaming flushes and FlatList never treats them as
    // removed-then-added.
    const withSeparators = [];
    let previousDayKey = null;
    for (const item of grouped) {
      const ts = item.type === 'group' ? item.messages?.[0]?.timestamp : item.timestamp;
      if (ts) {
        const d = new Date(ts);
        const dayKey = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
        if (dayKey !== previousDayKey) {
          withSeparators.push({
            type: 'date-separator',
            id: `date-separator-${dayKey}`,
            _stableKey: `date-separator-${dayKey}`,
            dayTimestamp: ts,
          });
          previousDayKey = dayKey;
        }
      }
      withSeparators.push(item);
    }

    return { items: withSeparators, nextGroups };
  }, [visibleMessages, isCoworkerChat]);
  const groupedMessages = groupedResult.items;
  groupedMessagesRef.current = groupedMessages;
  useEffect(() => {
    groupCacheRef.current = groupedResult.nextGroups;
  }, [groupedResult]);

  useEffect(() => {
    tracksAnchorInStateRef.current = !isCoworkerChat;
    workEventsReadyRef.current = !isCoworkerChat
      || !coworker.isActive
      || coworkerHasSnapshot
      || (coworker.events?.length > 0);
  }, [isCoworkerChat, coworker.isActive, coworkerHasSnapshot, coworker.events?.length]);

  // ── Conversation timeline rail (desktop web) ─────────────────────────────
  // One entry per user prompt; hovering the right-edge rail expands it into a
  // clickable list that jumps the list to that turn.
  //
  // When a contextual sidebar (document/presentation/spreadsheet editor,
  // PDF preview, etc.) is open, the chat pane can get too narrow for the rail
  // and its 288px hover panel to sit beside the message column without
  // overlapping content — so it hides until there's room again (resizing the
  // divider updates contextSidebarWidth live).
  const isContextSidebarVisible = useUIStore(state => state.isContextSidebarVisible);
  const contextSidebarWidth = useUIStore(state => getEffectiveContextSidebarWidth(state, windowWidth));
  const availableChatWidth = (windowWidth || 0) - (isContextSidebarVisible ? (contextSidebarWidth || 0) : 0);
  const showTimelineRail = Platform.OS === 'web'
    && !isXS
    && !isCompact
    && !isCoworkerChat
    && availableChatWidth >= 1150;

  // Center the transcript and composer between navigation and Sources.
  // Narrow them together before hiding the Sources card.
  const navigationSidebarWidth = useUIStore(getNavigationSidebarWidth);
  const showSourcesOutputsPref = useUIStore(state => state.showSourcesOutputsPanel !== false);
  const chatPaneWidth = (windowWidth || 0)
    - (isXS ? 0 : navigationSidebarWidth)
    - (isContextSidebarVisible ? (contextSidebarWidth || 0) : 0);
  const sourcesOutputsLayout = resolveSourcesOutputsLayout({
    chatPaneWidth,
    artifactOpen: isContextSidebarVisible && isArtifactSidebarType(contextSidebarType),
  });
  const showSourcesOutputsPanel = showSourcesOutputsPref
    && Platform.OS === 'web'
    && !isXS
    && !isCompact
    && sourcesOutputsLayout.canShowPanel;
  const composerFilesForSources = useChatInputStore(
    (state) => state.inputStates?.[id]?.files ?? EMPTY_COMPOSER_FILES
  );
  // The Sources card and the timeline rail share the right gutter. Feeding the
  // column height into the card bounds its scroll area, and the height it
  // reports back keeps the rail's hover panel from opening underneath it.
  const [messagesAreaHeight, setMessagesAreaHeight] = useState(0);
  const [sourcesPanelHeight, setSourcesPanelHeight] = useState(0);
  const handleMessagesAreaLayout = useCallback((event) => {
    const height = event?.nativeEvent?.layout?.height ?? 0;
    setMessagesAreaHeight(prev => (Math.abs(prev - height) < 1 ? prev : height));
  }, []);
  const handleSourcesPanelHeightChange = useCallback((height) => {
    const next = Number.isFinite(height) ? height : 0;
    setSourcesPanelHeight(prev => (Math.abs(prev - next) < 1 ? prev : next));
  }, []);
  // Structure-only key: the panel reads files / images / videos / citations /
  // artifacts, never message text, so streaming token flushes (which only
  // change content) must not re-collect the panel ~20 times a second.
  const sourcesStructureKey = useMemo(() => {
    if (!showSourcesOutputsPanel) return '';
    let key = '';
    for (const m of chatData.messages) {
      key += `${m?.id || ''}:${m?.files?.length || 0}:${m?.images?.length || 0}:${m?.videos?.length || 0}:${m?.citations?.length || 0}:${m?.parameters?.coworkerArtifacts?.length || 0}|`;
    }
    return key;
  }, [showSourcesOutputsPanel, chatData.messages]);
  const {
    inputs: conversationInputs,
    outputs: conversationOutputs,
    sources: conversationSources,
  } = useMemo(() => {
    if (!showSourcesOutputsPanel) return { inputs: [], outputs: [], sources: [] };
    const collected = collectSourcesAndOutputs(chatData.messages, composerFilesForSources);
    // Work replies carry no citations, so the pages the agent actually opened
    // are the thread's sources.
    if (!isCoworkerChat) return collected;
    const visited = workSourcesFromEvents(coworker.events).map((entry) => ({
      kind: 'link',
      id: `work-source-${entry.seq}`,
      url: entry.url,
      title: entry.host,
      hostname: entry.host,
    }));
    if (!visited.length) return collected;
    const known = new Set(collected.sources.map((entry) => entry.url).filter(Boolean));
    return {
      inputs: collected.inputs,
      outputs: collected.outputs,
      sources: [...collected.sources, ...visited.filter((entry) => !known.has(entry.url))],
    };
    // sourcesStructureKey stands in for chatData.messages: token streaming
    // changes content only, and content never feeds this panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSourcesOutputsPanel, sourcesStructureKey, composerFilesForSources, isCoworkerChat, coworker.events]);
  const hasSourcesOrOutputs = conversationInputs.length > 0
    || conversationOutputs.length > 0
    || conversationSources.length > 0;
  const setVideoProjectContext = useUIStore(state => state.setVideoProjectContext);
  const videoScriptArchiveRef = useRef({ conversationId: null, fetching: false, done: false });
  const videoStudioGateRef = useRef({ conversationId: null, autoOpened: false, dismissed: false });
  const videoProjectKey = useMemo(() => {
    let key = `${id || ''}:${coworker.taskId || ''}|`;
    for (const message of chatData.messages) {
      key += `${message?.id || ''}:${message?.files?.length || 0}:${message?.images?.length || 0}:${message?.videos?.length || 0}:${message?.parameters?.coworkerArtifacts?.length || 0}|`;
      for (const artifact of message?.parameters?.coworkerArtifacts || []) {
        key += `${artifact?.path || artifact?.name || ''}:${artifact?.size || 0}|`;
      }
      if (message?.role !== 'assistant') continue;
      const text = typeof message.content === 'string' ? message.content : '';
      if (text.length > 80 && /(?:shot|clip)\s+\d+/i.test(text)) {
        key += `${text.length}:${text.slice(0, 20)}:${text.slice(-20)}|`;
      }
    }
    return key;
  }, [id, coworker.taskId, chatData.messages]);
  const videoStudioProject = useMemo(() => {
    if (!isCoworkerChat) return null;
    const collected = collectVideoStudioInventory({ messages: chatData.messages,
      task: coworker.task, taskId: coworker.taskId, conversationId: id, summary: chat?.summary });
    const stored = useUIStore.getState().videoProjectContext;
    return buildVideoProject({
      artifacts: collected.artifacts,
      files: collected.files,
      scriptText: collected.scriptText || (stored?.conversationId === id ? stored.scriptText : '') || '',
    });
    // videoProjectKey stands in for chatData.messages so token streaming
    // does not rebuild the studio on every token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoworkerChat, videoProjectKey, coworker.task?.artifacts, coworker.task?.context, coworker.task?.referenceFiles]);
  const mediaStudioKind = useMemo(() => conversationMediaStudio({
    project: videoStudioProject,
    messages: chatData.messages,
  }), [videoStudioProject, videoProjectKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!coworker.taskId || !coworker.followUpQueue?.length) return;
    for (const item of coworker.followUpQueue) {
      if (isObsoleteVideoFollowUp(item.text, { events: coworker.events, project: videoStudioProject })) {
        coworker.removeFollowUp(item.id);
      }
    }
  }, [coworker.events, coworker.followUpQueue, coworker.removeFollowUp, coworker.taskId, videoStudioProject]);
  const [videoReviewHiddenGate, setVideoReviewHiddenGate] = useState(null);
  useEffect(() => {
    setVideoReviewHiddenGate(null);
    useUIStore.getState().clearVideoTakePicks();
  }, [id, coworker.taskId]);
  useEffect(() => {
    const gate = videoProjectReviewGate(videoStudioProject, { events: coworker.events });
    if (videoReviewHiddenGate && gate !== videoReviewHiddenGate) {
      setVideoReviewHiddenGate(null);
    }
  }, [coworker.events, videoStudioProject, videoReviewHiddenGate]);
  const videoReviewQuestion = useMemo(() => {
    if (!isCoworkerChat || !coworker.taskId || coworker.isActive) return null;
    if (coworker.browserState?.question || coworker.browserState?.confirmation) return null;
    const gate = videoProjectReviewGate(videoStudioProject, { events: coworker.events });
    if (!gate || gate === videoReviewHiddenGate) return null;
    return workVideoReviewQuestion(videoStudioProject, { events: coworker.events });
  }, [
    coworker.browserState?.confirmation,
    coworker.browserState?.question,
    coworker.events,
    coworker.isActive,
    coworker.taskId,
    isCoworkerChat,
    videoReviewHiddenGate,
    videoStudioProject,
  ]);
  const composerQuestion = coworker.browserState?.question || videoReviewQuestion;
  useEffect(() => {
    const collected = collectVideoStudioInventory({ messages: chatData.messages,
      task: coworker.task, taskId: coworker.taskId, conversationId: id, summary: chat?.summary });
    const previous = useUIStore.getState().videoProjectContext;
    const keptScript = previous?.conversationId === id ? previous.scriptText : '';
    const ctx = {
      conversationId: id,
      taskId: coworker.taskId || null,
      artifacts: collected.artifacts,
      files: collected.files,
      scriptText: collected.scriptText || keptScript || '',
    };
    setVideoProjectContext(ctx);
    const ui = useUIStore.getState();
    const studioOpen = ui.isContextSidebarVisible
      && ui.contextSidebarType === 'video-project-preview'
      && (!id || String(ui.contextSidebarProps?.conversationId || '') === String(id));
    if (videoStudioGateRef.current.conversationId !== id) {
      videoStudioGateRef.current = { conversationId: id, autoOpened: studioOpen, dismissed: false };
    }
    const gate = videoStudioGateRef.current;
    if (gate.autoOpened && !studioOpen) gate.dismissed = true;
    if (studioOpen) gate.autoOpened = true;
    if (studioOpen) {
      maybeAutoOpenVideoProject({ ...ctx, onlyIfOpen: true });
    } else if (mediaStudioKind === 'video' && !gate.dismissed && firstVideoProjectMedia(ctx)) {
      if (maybeAutoOpenVideoProject(ctx)) gate.autoOpened = true;
    }
    const archivedCount = chat?.contextWindow?.archivedMessageCount || chat?.stats?.archivedMessages || 0;
    if (videoScriptArchiveRef.current.conversationId !== id) {
      videoScriptArchiveRef.current = { conversationId: id, fetching: false, done: false };
    }
    const archiveGate = videoScriptArchiveRef.current;
    if (
      isCoworkerChat
      && id
      && !ctx.scriptText
      && archivedCount > 0
      && !archiveGate.done
      && !archiveGate.fetching
    ) {
      archiveGate.fetching = true;
      const isCurrent = () => videoScriptArchiveRef.current === archiveGate
        && useUIStore.getState().videoProjectContext?.conversationId === id;
      loadVideoStudioScript({ conversationId: id, fetchPage: chatsApi.getArchivedConversationMessages, isCurrent })
        .then((scriptText) => {
          archiveGate.fetching = false;
          archiveGate.done = true;
          if (!scriptText || !isCurrent()) return;
          const next = {
            ...(useUIStore.getState().videoProjectContext || {}),
            conversationId: id,
            taskId: coworker.taskId || null,
            scriptText,
          };
          setVideoProjectContext(next);
          maybeAutoOpenVideoProject({ ...next, onlyIfOpen: true });
        })
        .catch(() => {
          archiveGate.fetching = false;
          archiveGate.done = true;
        });
    }
    // videoProjectKey stands in for chatData.messages so token streaming
    // does not rebuild the studio on every token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoProjectKey,
    coworker.task?.artifacts,
    coworker.task?.context,
    coworker.task?.referenceFiles,
    id,
    coworker.taskId,
    setVideoProjectContext,
    chat?.summary,
    isCoworkerChat,
    mediaStudioKind,
    chat?.contextWindow?.archivedMessageCount,
    chat?.stats?.archivedMessages,
  ]);
  useEffect(() => () => {
    const current = useUIStore.getState().videoProjectContext;
    if (current?.conversationId === id) setVideoProjectContext(null);
  }, [id, setVideoProjectContext]);
  const openBrowserViewer = useCoworkerStore(state => state.openBrowserViewer);
  const closeBrowserViewer = useCoworkerStore(state => state.closeBrowserViewer);
  const browserViewerTaskId = useCoworkerStore(state => state.browserViewerTaskId);
  const browserViewerWatch = useCoworkerStore(state => state.browserViewerWatch);
  const showBrowserPanel = (Platform.OS === 'web' || Platform.OS === 'macos') && !isXS && isCoworkerChat && !!coworker.taskId && browserViewerTaskId === coworker.taskId;
  const handleOpenWorkBrowser = useCallback(
    () => openBrowserViewer(coworker.taskId, { watch: true }),
    [openBrowserViewer, coworker.taskId],
  );
  const workCloudBrowser = useMemo(
    () => ((isCoworkerChat && coworker.isActive && coworker.hasBrowserActivity)
      ? { taskId: coworker.taskId, onOpen: handleOpenWorkBrowser, isLive: coworker.isActive }
      : null),
    [isCoworkerChat, coworker.hasBrowserActivity, coworker.isActive, coworker.taskId, handleOpenWorkBrowser],
  );
  const conversationOutputFiles = useMemo(
    () => conversationOutputs.flatMap((entry) => (entry.file ? [entry.file] : [])),
    [conversationOutputs]
  );
  const imageStudioFile = useMemo(() => conversationOutputFiles.find(isImageMedia)
    || chatData.messages.filter((message) => message?.role === 'assistant')
      .flatMap((message) => message.files || []).find(isImageMedia),
  [conversationOutputFiles, chatData.messages]);
  const handleOpenVideoStudio = useCallback(() => {
    const stored = useUIStore.getState().videoProjectContext;
    const ctx = stored?.conversationId === id ? stored : {};
    const files = (ctx.files && ctx.files.length) ? ctx.files : conversationOutputFiles;
    maybeAutoOpenVideoProject({
      conversationId: id,
      taskId: coworker.taskId || ctx.taskId || null,
      artifacts: ctx.artifacts,
      files,
      scriptText: ctx.scriptText,
      file: firstVideoProjectMedia({ files, artifacts: ctx.artifacts }) || files.find(isImageMedia),
      videoProject: true,
    });
  }, [conversationOutputFiles, coworker.taskId, id]);
  const handleOpenImageStudio = useCallback(() => {
    if (imageStudioFile) openMessageFile(imageStudioFile, { conversationId: id, videoProject: false });
  }, [imageStudioFile, id]);
  const workVideoStudio = useMemo(() => {
    if (mediaStudioKind === 'video') return { kind: 'video', onOpen: handleOpenVideoStudio };
    if (imageStudioFile) return { kind: 'image', onOpen: handleOpenImageStudio };
    return null;
  }, [imageStudioFile, handleOpenVideoStudio, handleOpenImageStudio, mediaStudioKind]);

  // Scheduled tasks bound to this Work thread (same records as /tasks).
  const workChatTasks = useWorkChatTasks({
    workSessionId: coworker.taskId,
    conversationId: id,
    chatTitle,
    enabled: isCoworkerChat && isAuthenticated && isOwner && showSourcesOutputsPanel,
  });
  const hasVisibleSourcesPanel = showSourcesOutputsPanel
    && (hasSourcesOrOutputs || !!workCloudBrowser || !!workVideoStudio || isCoworkerChat);
  const sourcesReservedRight = hasVisibleSourcesPanel && !showBrowserPanel ? sourcesOutputsLayout.reservedRight : 0;
  // The rail's hover card must start below the Sources card.
  const timelineTopInset = hasVisibleSourcesPanel && sourcesPanelHeight > 0
    ? SOURCES_OUTPUTS_PANEL_TOP + sourcesPanelHeight
    : 0;
  // The composer is an absolutely-positioned sibling over this column. The
  // rail has to stop above it or the last prompts in a long thread sit
  // behind the input and cannot be clicked.
  const timelineBottomInset = (isAuthenticated && isOwner && !isDetailed)
    ? inputContainerHeight + workDockGap
    : 0;

  // Stale anchors from the previous conversation must not light up a tick in
  // the next one.
  useEffect(() => {
    viewableAnchorIdRef.current = null;
    setViewableAnchorId(null);
  }, [id]);

  const timelinePrompts = useMemo(() => {
    if (!showTimelineRail) return [];
    const prompts = [];
    for (const m of chatData.messages) {
      if (m.role !== 'user' || !m.id) continue;
      if (isWorkFollowUpQueueMessage(m)) continue;
      const raw = typeof m.content === 'string' ? m.content : '';
      // Only the first line of the preview is shown, so bounding the parse
      // keeps this cheap during streaming re-renders.
      let text = raw ? markdownToPlainText(raw.slice(0, 400)).replace(/\s+/g, ' ').trim() : '';
      if (!text) text = t('chatDetails.nonTextMessage');
      prompts.push({ id: m.id, text });
    }
    return prompts;
  }, [showTimelineRail, chatData.messages, t]);

  // Active prompt = the last user turn at or above the top-most visible
  // message (assistant replies map back to the prompt that produced them).
  const activeTimelinePromptId = useMemo(() => {
    if (!viewableAnchorId || timelinePrompts.length === 0) return null;
    let active = null;
    for (const m of chatData.messages) {
      if (m.role === 'user' && m.id && !isWorkFollowUpQueueMessage(m)) active = m.id;
      if (m.id === viewableAnchorId || m._id === viewableAnchorId) break;
    }
    return active;
  }, [viewableAnchorId, chatData.messages, timelinePrompts.length]);

  const handleTimelinePromptPress = useCallback((messageId) => {
    requestScrollToMessage({ conversationId: id, messageId, viewPosition: 0 });
  }, [requestScrollToMessage, id]);

  // Image-tool chips and "try another model" are chat-only. Gate on the live
  // Work flag — not just the handover-delayed chrome — so a thread that just
  // became Work does not keep suggesting /tools/* while the composer fades.
  const hideMediaSuggestions = isCoworkerChat || coworker.isCoworkerChat;

  const getImageRequestQueryForAssistant = useCallback((message) => {
    if (hideMediaSuggestions) return null;
    return resolveImageRequestUserQuery(message, chatData.messages);
  }, [chatData.messages, hideMediaSuggestions]);

  const getImageRequestQueryForGroup = useCallback((group) => {
    if (hideMediaSuggestions) return null;
    return resolveImageRequestUserQueryForGroup(group, chatData.messages);
  }, [chatData.messages, hideMediaSuggestions]);

  // A Work run picks its own model for each step and owns a task across many
  // turns, so "try this with another model" has nothing to re-run: it would
  // fork a new chat turn into the middle of the agent's work.
  const getMediaGenerationContextForAssistant = useCallback((message) => {
    if (hideMediaSuggestions) return null;
    return resolveMediaGenerationContext(message, chatData.messages);
  }, [chatData.messages, hideMediaSuggestions]);

  const getMediaGenerationContextForGroup = useCallback((group) => {
    if (hideMediaSuggestions) return null;
    return resolveMediaGenerationContextForGroup(group, chatData.messages);
  }, [chatData.messages, hideMediaSuggestions]);

  // One-click "try with another model": send a NEW user turn with the same
  // prompt so the previous generation stays visible for comparison.
  const handleTryMediaModel = useCallback(async ({ modelId, model, prompt, action, fileIds = [] }) => {
    if (!prompt?.trim() || !modelId) return;

    if (isHeldForReview) {
      openModal('AccountReviewHoldModal', { source: 'media_model_suggestion' });
      return;
    }

    if (!(await socketManager.requireConnection())) {
      return;
    }

    if (!isGhost && needsPhoneVerification) {
      openModal('VerifyPhoneModal', { source: 'media_model_suggestion' });
      return;
    }

    if (!isGhost && needsEmailVerification) {
      openModal('VerifyEmailModal', { source: 'media_model_suggestion' });
      return;
    }

    if (!isPlus && !isGhost && !hasAvailableCredits(totalAvailableCredits)) {
      openCreditsRecoveryModal(openModal, user, {
        source: 'media_model_suggestion',
        title: "Out of credits",
        subtitle: `You've used all your free credits. Upgrade to Plus for free default model messages and ${PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits.`,
      });
      return;
    }

    if (isModelLockedForUser(user, model)) {
      openModelAccessPaywall(openModal, {
        model,
        title: 'Unlock this model',
        subtitle: 'To use this model, buy credits or upgrade to Plus.',
      });
      return;
    }

    // Snapshot composer selection so history-seeding from this one-shot
    // message can't pin the tried model into the picker afterward.
    const inputStore = useChatInputStore.getState();
    const inputState = inputStore.inputStates?.[id];
    const prevModelIds = Array.isArray(inputState?.selectedModelIds)
      ? [...inputState.selectedModelIds]
      : [];
    const wasAuto = Boolean(inputState?.userExplicitAutoMode) || prevModelIds.length === 0;

    // Send with the clicked model for this turn only. ___oneShotModel tells
    // seedModelsFromConversationHistory to ignore this message when restoring
    // the composer from history (keeps Auto / prior selection intact).
    const formattedMessage = {
      text: prompt.trim(),
      modelIds: [modelId],
      agentIds: [],
      actions: [],
      fileIds: Array.isArray(fileIds) ? fileIds.filter(Boolean) : [],
      parameters: {
        ...(action ? { action } : {}),
        ___oneShotModel: true,
      },
    };

    const emitOk = socketManager.emitSendUserMessage({
      userId,
      formattedMessage,
      conversationId: id,
    });
    if (emitOk === false) return;

    // Lock Auto (or restore the prior selection) so a later history-seed from
    // this message can't rewrite the composer.
    if (inputStore.currentContextId === id) {
      if (wasAuto) {
        inputStore.markContextExplicitAuto?.(id);
        if ((inputStore.inputStates?.[id]?.selectedModelIds || []).length > 0) {
          inputStore.setSelectedModels([]);
        }
      } else {
        inputStore.setSelectedModels(prevModelIds);
      }
    }

    abortScrollRestore();
    requestPinToBottomRef.current?.(true, 900, 5000);
  }, [
    id,
    userId,
    isPlus,
    isGhost,
    isHeldForReview,
    needsEmailVerification,
    needsPhoneVerification,
    totalAvailableCredits,
    openModal,
    user,
    abortScrollRestore,
  ]);

  const handleActivateWorkFromComposer = useCallback(() => {
    requireAuth(async () => {
      const text = String(workComposerText || '').trim();
      const fileIds = getFileIds(workComposerFiles);
      if (!text && !fileIds.length) return;
      const created = await startWorkFromPrompt({
        prompt: text,
        fileIds,
        collectionId: chat?.inCollectionId,
        agentId: chat?.agentId,
        router,
        source: 'composer_chip',
      });
      if (created) {
        useChatInputStore.getState().clearInput(true);
      }
    }, {
      source: 'composer_work_mode_suggestion',
      title: t('chatInput.coworkerMode.authTitle'),
      subtitle: t('chatInput.coworkerMode.authSubtitle'),
      icon: 'robot',
    });
  }, [requireAuth, workComposerText, workComposerFiles, chat?.inCollectionId, chat?.agentId, router, t]);

  const handleSendPromptToWork = useCallback(async ({ prompt, fileIds }) => {
    const gated = requireAuth(() => true, {
      source: 'response_work_mode_suggestion',
      title: t('chatInput.coworkerMode.authTitle'),
      subtitle: t('chatInput.coworkerMode.authSubtitle'),
      icon: 'robot',
    });
    if (gated !== true) return;
    await startWorkFromPrompt({
      prompt,
      fileIds,
      collectionId: chat?.inCollectionId,
      agentId: chat?.agentId,
      router,
      source: 'response_card',
    });
  }, [requireAuth, chat?.inCollectionId, chat?.agentId, router, t]);

  const handleWorkModeChoice = useCallback(async (choice, messageId) => {
    if (!isOwner || isInferencing || !(await socketManager.requireConnection())) return;
    socketManager.emitKickConversation(userId, id, {
      workModeChoice: choice,
      workModeMessageId: String(messageId),
    });
  }, [isOwner, isInferencing, userId, id]);

  const hasConversationSummaryPill = useMemo(() => {
    return typeof chat?.summary === 'string' && chat.summary.trim().length > 0;
  }, [chat?.summary]);

  const findGroupedIndex = useCallback((items, targetMessageId) => {
    return findGroupedMessageIndex(items, targetMessageId);
  }, []);

  // Re-pin an active restore anchor (non-animated, so corrections are
  // invisible). Called from onContentSizeChange while rows above the anchor
  // are still settling their heights. Self-expires via `until`.
  const repinAnchorNow = useCallback(() => {
    const pin = anchorPinRef.current;
    if (!pin) return;
    if (Date.now() > pin.until) {
      anchorPinRef.current = null;
      return;
    }
    if (!flatListRef.current?.scrollToIndex) return;
    const index = findGroupedIndex(groupedMessages, pin.messageId);
    if (index < 0) return;
    const listIndex = index + (hasConversationSummaryPill ? 1 : 0);
    try {
      flatListRef.current.scrollToIndex({ index: listIndex, animated: false, viewPosition: pin.viewPosition });
    } catch (_) {
      // Item not laid out yet — the next content-size change retries.
    }
  }, [groupedMessages, findGroupedIndex, hasConversationSummaryPill]);

  // Handle scroll requests from other UI (contextual sidebar, etc.)
  useEffect(() => {
    if (isCompact) return;
    if (!pendingScrollRequest) return;
    if (pendingScrollRequest.conversationId !== id) return;

    // An explicit scroll-to-message must win over any active bottom-pin (e.g.
    // from the just-opened conversation's initial load). Clear up-front so the
    // pin can't fight the scrollToIndex retries while we wait for the target
    // item to be laid out.
    clearBottomPin();

    const targetMessageId = pendingScrollRequest.messageId;
    const scrollViewPosition = pendingScrollRequest.viewPosition ?? 0.25;
    const scrollAnimated = pendingScrollRequest.animated !== false;
    const shouldPinAnchor = pendingScrollRequest.pinAnchor === true;
    const targetIsArchived = pendingScrollRequest.isArchivedMessage === true;

    // Cold-start restore (instant jump + anchor pin): veil the list during
    // positioning so the settle corrections are invisible. Explicit animated
    // jumps (sidebar / search) keep their visible scroll.
    if (shouldPinAnchor && !scrollAnimated) {
      beginListSettleVeil(2500);
    }
    const archiveOriginalIndex = Number.isFinite(pendingScrollRequest.archiveOriginalIndex)
      ? pendingScrollRequest.archiveOriginalIndex
      : null;
    const hydrationKey = `${id}:${targetMessageId}:${pendingScrollRequest.requestedAt}`;
    if (archiveScrollHydrationRef.current.key !== hydrationKey) {
      archiveScrollHydrationRef.current = {
        key: hydrationKey,
        didTargetedLoad: false,
        loadsRemaining: archiveOriginalIndex != null ? 6 : 60,
      };
    }
    let attemptsRemaining = 20;
    let cancelled = false;
    let retryTimer = null;

    const scheduleRetry = (delayMs) => {
      // Clear any previously scheduled retry — only the latest may fire, and
      // the cleanup below can only clear the timer still referenced here.
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        tryScroll();
      }, delayMs);
    };

    const loadArchivedTargetPage = async () => {
      const hydrationState = archiveScrollHydrationRef.current;
      if (!targetIsArchived || hydrationState.loadsRemaining <= 0) return false;

      const latestChat = useChatStore.getState().chats.find(c => c.id === id);
      const latestPagination = latestChat?.archivePagination || {};
      const latestMessages = latestChat?.messages || [];
      const targetAlreadyLoaded = latestMessages.some(message => (
        message?.id === targetMessageId || message?._id === targetMessageId
      ));
      if (targetAlreadyLoaded) return true;
      if (latestPagination.isLoading) return true;

      const archivedCount = latestChat?.contextWindow?.archivedMessageCount
        || latestChat?.stats?.archivedMessages
        || 0;
      const loadedArchivedCount = latestPagination.loadedCount
        || latestMessages.filter(message => message.isArchivedMessage).length
        || 0;
      if (latestPagination.hasMore === false || loadedArchivedCount >= archivedCount) {
        return false;
      }

      hydrationState.loadsRemaining -= 1;
      try {
        if (!hydrationState.didTargetedLoad && archiveOriginalIndex != null) {
          hydrationState.didTargetedLoad = true;
          await fetchArchivedMessages(id, {
            beforeIndex: archiveOriginalIndex + 1,
            limit: 25,
            preserveCursor: true,
          });
        } else {
          await fetchArchivedMessages(id, { limit: 100 });
        }
        return true;
      } catch {
        return false;
      }
    };

    const tryScroll = async () => {
      if (cancelled) return;
      const index = findGroupedIndex(groupedMessages, targetMessageId);

      if (index >= 0 && flatListRef.current?.scrollToIndex) {
        try {
          const listIndex = index + (hasConversationSummaryPill ? 1 : 0);
          flatListRef.current.scrollToIndex({ index: listIndex, animated: scrollAnimated, viewPosition: scrollViewPosition });
          if (shouldPinAnchor) {
            // Hold the anchor in place while late-rendering rows settle;
            // onContentSizeChange re-pins until the deadline.
            anchorPinRef.current = {
              messageId: targetMessageId,
              viewPosition: scrollViewPosition,
              until: Date.now() + 3000,
            };
          }
          // Virtualized web lists often accept scrollToIndex before the row
          // is in the DOM. Retry until the highlight node can scrollIntoView.
          if (Platform.OS === 'web') {
            const inView = scrollMessageNodeIntoView(targetMessageId, { animated: false });
            if (!inView && attemptsRemaining > 0) {
              attemptsRemaining -= 1;
              scheduleRetry(120);
              return;
            }
          }
          clearPendingScrollRequest();
        } catch (e) {
          if (attemptsRemaining > 0) {
            attemptsRemaining -= 1;
            scheduleRetry(150);
          } else {
            scrollMessageNodeIntoView(targetMessageId, { animated: false });
            clearPendingScrollRequest();
          }
        }
      } else if (attemptsRemaining > 0) {
        attemptsRemaining -= 1;
        scheduleRetry(150);
      } else {
        const requestedArchivePage = await loadArchivedTargetPage();
        if (cancelled) return;
        if (requestedArchivePage) {
          attemptsRemaining = 12;
          scheduleRetry(150);
        } else {
          scrollMessageNodeIntoView(targetMessageId, { animated: false });
          clearPendingScrollRequest();
        }
      }
    };

    scheduleRetry(initialLoadComplete ? 0 : 150);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [pendingScrollRequest?.requestedAt, id, groupedMessages, initialLoadComplete, findGroupedIndex, isCompact, clearBottomPin, clearPendingScrollRequest, fetchArchivedMessages, hasConversationSummaryPill, beginListSettleVeil]);

  // Handle scroll-to-top requests
  useEffect(() => {
    if (isCompact) return;
    if (!pendingScrollToTopRequest) return;
    if (pendingScrollToTopRequest.conversationId !== id) return;

    // Scroll-to-top must win over any active bottom-pin.
    clearBottomPin();
    if (flatListRef.current?.scrollToOffset) {
      flatListRef.current.scrollToOffset({ offset: 0, animated: true });
    }
    clearPendingScrollToTopRequest();
  }, [pendingScrollToTopRequest?.requestedAt, id, clearPendingScrollToTopRequest, isCompact, clearBottomPin]);

  // Memoize render item
  const lastIndex = useMemo(() => {
    if (!groupedMessages.length) return 0;
    return groupedMessages.length - 1 + (hasConversationSummaryPill ? 1 : 0);
  }, [groupedMessages.length, hasConversationSummaryPill]);
  const renderItem = useCallback(({ item, index }) => {
    // Live Work run: one status row at the end of the transcript saying what
    // the agent is doing right now. Completed steps live in the trails above.
    if (item.type === 'work-live-activity') {
      return (
        <WorkLiveActivityRow
          style={[workColumnStyle, Platform.OS === 'web' && { overflowAnchor: 'none' }]}
          live={workRuns?.live}
        />
      );
    }
    if (item.type === 'work-upgrade-notice') {
      return <WorkUpgradeNotice style={workColumnStyle} />;
    }
    // Gate cards only exist while a live run is waiting on the user, so a
    // mount ease-in is always the right entrance.
    if (item.type === 'work-approval') {
      return (
        <WorkAppear style={workColumnStyle} duration={220} distance={6}>
          <WorkApprovalCard
            taskId={coworker.taskId}
            confirmation={item.confirmation}
            onDismissQuestion={coworker.dismissPendingGate}
            onRestoreQuestion={coworker.restorePendingGate}
          />
        </WorkAppear>
      );
    }
    if (item.type === 'work-question') {
      return (
        <WorkAppear style={workColumnStyle} duration={220} distance={6}>
          <WorkQuestionCard
            key={item.question?.id || coworker.taskId}
            taskId={coworker.taskId}
            question={item.question}
            onAnswered={(text, extra) => {
              if (String(item.question?.id || '').startsWith('video-review-')) {
                setVideoReviewHiddenGate(videoProjectReviewGate(videoStudioProject, { events: coworker.events }));
              }
              handleWorkQuestionAnswered(text, extra);
            }}
            onDismissQuestion={coworker.dismissPendingGate}
            onRestoreQuestion={coworker.restorePendingGate}
          />
        </WorkAppear>
      );
    }
    if (item.type === 'work-plan') {
      return (
        <WorkAppear style={[workColumnStyle, Platform.OS === 'web' && { overflowAnchor: 'none' }]} duration={220} distance={6}>
          <WorkPlanCard
            taskId={coworker.taskId}
            plan={item.plan}
            onAnswered={handleWorkQuestionAnswered}
            onDismissQuestion={coworker.dismissPendingGate}
            onRestoreQuestion={coworker.restorePendingGate}
          />
        </WorkAppear>
      );
    }
    if (item.isInferencingWaiting || item.id === 'inferencing-waiting') {
      return (
        <MessageWaiting 
          conversationId={item.conversationId}
          inferenceStatus={item.inferenceStatus}
          inferenceStage={item.inferenceStage}
          inferenceElapsedMs={item.inferenceElapsedMs}
          canFastAnswer={item.canFastAnswer}
        />
      );
    }
    if (item.id === 'ghost-message') {
      return <MessageGhostUpsell />
    }
    if (item.type === 'card-expiration-warning') {
      return (
        <CardExpirationWarningMessage
          warningData={cardExpirationWarning}
          onDismiss={handleDismissCardExpirationWarning}
        />
      );
    }
    if (item.type === 'conversation-summary-pill') {
      return (
        <ConversationSummaryPill
          summary={item.summary}
          summarizedMessageCount={item.summarizedMessageCount}
          messageWidth={messageWidth}
        />
      );
    }
    if (item.type === 'date-separator') {
      return <MessageDateSeparator timestamp={item.dayTimestamp} messageWidth={messageWidth} />;
    }

    if (item.type === 'group') {
      return (
        <MessageHighlightWrapper messageIds={item.messages.flatMap(m => [m.id, m._id]).filter(Boolean)}>
          <View>
            <MessageGroup
              messages={item.messages}
              parentMessageId={item.parentMessageId}
              conversationId={id}
              isOwner={isOwner}
              chatTitle={chatTitle}
              isLastGroup={index === lastIndex}
            />
            {index !== lastIndex && !hideMediaSuggestions && (() => {
              const mediaContext = getMediaGenerationContextForGroup(item);
              const imageRequestQuery = getImageRequestQueryForGroup(item);
              if (!mediaContext && !imageRequestQuery) return null;
              return (
                <MediaGenerationSuggestions
                  mediaContext={mediaContext}
                  userQuery={imageRequestQuery}
                  onTryModel={handleTryMediaModel}
                  canTryModels={isOwner && !isInferencing}
                />
              );
            })()}
          </View>
        </MessageHighlightWrapper>
      );
    }

    if (!item.id && item.type !== 'group') {
      console.warn('Message missing ID:', item);
      return null;
    }

    const workRun = isCoworkerChat ? workRuns?.byMessageId?.[item.id] : null;
    const displayItem = isCoworkerChat && item.role === 'user'
      ? workUserMessageForDisplay(item)
      : item;
    if (isCoworkerChat && item.role === 'assistant') {
      return (
        <WorkTranscriptMessage
          item={item}
          index={index}
          lastIndex={lastIndex}
          conversationId={id}
          isOwner={isOwner}
          chatTitle={chatTitle}
          workRun={workRun}
          workColumnStyle={workColumnStyle}
          taskStatus={coworker.taskStatus}
          HighlightWrapper={MessageHighlightWrapper}
        />
      );
    }

    // A user request in a Work thread carries the run's header and the steps
    // taken right after it — the timeline stays chronological around steers.
    if (isCoworkerChat && item.role === 'user' && workRun) {
      return (
        <MessageHighlightWrapper messageIds={[item.id]}>
          <WorkUserTranscriptRow
            workRun={workRun}
            workColumnStyle={workColumnStyle}
            taskStatus={coworker.taskStatus}
          >
            <Message
              conversationId={id}
              isOwner={isOwner}
              chatTitle={chatTitle}
              message={displayItem}
              isLastMessage={index === lastIndex}
            />
          </WorkUserTranscriptRow>
        </MessageHighlightWrapper>
      );
    }

    return (
      <MessageHighlightWrapper messageIds={[item.id, item._id].filter(Boolean)}>
        <View>
          <Message
            conversationId={id}
            isOwner={isOwner}
            chatTitle={chatTitle}
            message={displayItem}
            isLastMessage={index === lastIndex}
          />
          {index !== lastIndex && item.role === 'assistant' && !hideMediaSuggestions && (() => {
            const mediaContext = getMediaGenerationContextForAssistant(item);
            const imageRequestQuery = getImageRequestQueryForAssistant(item);
            if (!mediaContext && !imageRequestQuery) return null;
            return (
              <MediaGenerationSuggestions
                mediaContext={mediaContext}
                userQuery={imageRequestQuery}
                onTryModel={handleTryMediaModel}
                canTryModels={isOwner && !isInferencing}
              />
            );
          })()}
        </View>
      </MessageHighlightWrapper>
    );
  }, [id, isOwner, chatTitle, lastIndex, cardExpirationWarning, handleDismissCardExpirationWarning, messageWidth, getImageRequestQueryForAssistant, getImageRequestQueryForGroup, getMediaGenerationContextForAssistant, getMediaGenerationContextForGroup, handleTryMediaModel, hideMediaSuggestions, isInferencing, isCoworkerChat, workRuns, workColumnStyle, coworker.taskId, coworker.taskStatus, handleWorkQuestionAnswered, videoStudioProject]);

  // Set on the first genuine user drag (never by programmatic scrolls), so
  // the top-threshold auto-load below can't be triggered by the list's own
  // positioning scrolls (initial bottom-pin, anchor re-pins).
  const hasUserDraggedRef = useRef(false);
  // Latest { can, run } for the archived-messages auto-load, refreshed every
  // render so handleScroll (stable identity) always reads current state.
  const autoLoadEarlierRef = useRef({ can: false, run: null });

  const handleScroll = useCallback((event) => {
    const { contentOffset, contentSize, layoutMeasurement } = event?.nativeEvent || {};
    const el = getScrollElement();
    const offsetY = contentOffset?.y ?? el?.scrollTop;
    const contentH = contentSize?.height ?? el?.scrollHeight;
    const layoutH = layoutMeasurement?.height ?? el?.clientHeight;
    if (offsetY == null || !contentH || !layoutH) return;

    // Keep offset/layout refs fresh on every event (even throttled) so the pin
    // release heuristic below has up-to-date data.
    const prevOffset = scrollOffsetRef.current;
    if (restoreStudioScrollIfSnapped(offsetY, el)) return;
    const looksLikeRemountSnap = offsetY < 32 && prevOffset > 80;
    const userIntent = isUserScrollingRef.current || Date.now() <= userScrollIntentUntilRef.current;
    if (looksLikeRemountSnap && !userIntent) {
      listLayoutHeightRef.current = layoutH;
      return;
    }
    if (offsetY > prevOffset + 2) lastDownwardScrollAtRef.current = Date.now();
    scrollOffsetRef.current = offsetY;
    listLayoutHeightRef.current = layoutH;

    const canScroll = contentH > layoutH + 20;
    const bottomThreshold = Math.max(100, (listPaddingBottom || 0) + 20);
    const isAtBottom = !canScroll || (offsetY + layoutH >= contentH - bottomThreshold);
    isAtBottomRef.current = isAtBottom;

    // Any upward scroll by the user disarms follow-live and cancels any active
    // bottom pin so user navigation always wins over agent activity.
    //
    // The offset also drops without any gesture when content *shrinks* while
    // we sit at the bottom (a question card resolving, a pending send row
    // being replaced by the shorter server row, a thinking block collapsing):
    // the browser clamps scrollTop to the new maximum. Treating that clamp as
    // "user scrolled up" switched follow-live off right as the agent started
    // streaming, so the next growth left the viewport parked above the tail.
    // A real upward scroll never lands exactly on the max offset (and the
    // wheel/drag handlers already disarm follow-live for genuine gestures).
    const maxOffset = Math.max(0, contentH - layoutH);
    const clampedToMax = offsetY >= maxOffset - 2;
    if (offsetY < prevOffset - 2 && !clampedToMax && userIntent) {
      followLiveRef.current = false;
      if (wantsBottomRef.current) clearBottomPin();
      abortScrollRestore();
    }

    // Auto-load earlier messages when the user scrolls into the top threshold.
    // Driven by real scroll events (gated on an actual drag) rather than
    // FlatList's onStartReached: that prop re-fires on every content-size
    // change while parked near the top, and with uncompensated prepends it
    // cascaded — each page shifted the viewport up, re-arming the next load
    // until the user landed at the very top of the thread. Here, the prepend
    // compensation moves the offset back out of the threshold.
    if (
      hasUserDraggedRef.current
      && offsetY <= Math.min(layoutH * 0.5, 350)
      && autoLoadEarlierRef.current.can
    ) {
      autoLoadEarlierRef.current.run?.();
    }

    const now = Date.now();
    if (now - lastScrollTime.current < 50) {
      return;
    }
    lastScrollTime.current = now;

    // Scrolling back down into the tail re-arms follow — but only for a real
    // gesture. Programmatic pins also move the offset down into the tail, and
    // their scroll event can land just after the user began dragging up; the
    // drag already disarmed follow-live, so honoring that echo re-armed it and
    // the next flush yanked the user back to the bottom.
    const recentProgrammaticPin = now - lastProgrammaticPinAtRef.current < 250;
    const hasUserIntent = isUserScrollingRef.current || now <= userScrollIntentUntilRef.current;
    if (!mobileReadingChat && isAtBottom && offsetY > prevOffset + 2 && hasUserIntent && !recentProgrammaticPin) {
      followLiveRef.current = true;
    }
    scheduleShowScrollButton(canScroll && !isAtBottom);
    const nearTop = offsetY < 10;
    setIsNearTop(nearTop);
  }, [clearBottomPin, abortScrollRestore, getScrollElement, listPaddingBottom, restoreStudioScrollIfSnapped, scheduleShowScrollButton, mobileReadingChat]);

  const scrollToBottom = useCallback(() => {
    // Tapping the down-arrow is an explicit "take me to the bottom" — abort any
    // in-flight cold-start restore first, otherwise on long chats its retry
    // loop snaps the viewport back up to the saved anchor right after we land
    // at the bottom. Then jump and keep re-pinning while async content
    // (markdown, code, images) finishes laying out so we reach the true bottom.
    abortScrollRestore();
    requestPinToBottom(false, 700, 3500, { followLive: true });
  }, [requestPinToBottom, abortScrollRestore]);

  const archivedMessageCount = chat?.contextWindow?.archivedMessageCount || chat?.stats?.archivedMessages || 0;
  const loadedArchivedMessageCount = chat?.archivePagination?.loadedCount
    || (chat?.messages || []).filter(message => message.isArchivedMessage).length
    || 0;
  const canLoadArchivedMessages = !isCompact
    && isChatInStore
    && archivedMessageCount > 0
    && loadedArchivedMessageCount < archivedMessageCount
    && chat?.archivePagination?.hasMore !== false;
  const isLoadingArchivedMessages = chat?.archivePagination?.isLoading === true;

  const findTopVisibleMessageId = useCallback(() => {
    const container = getScrollElement();
    if (container) {
      try {
        const containerTop = container.getBoundingClientRect().top;
        const msgEls = container.querySelectorAll('[id^="msg-hl-"]');
        for (let i = 0; i < msgEls.length; i++) {
          const rect = msgEls[i].getBoundingClientRect();
          if (rect.bottom > containerTop + 10) {
            const msgId = msgEls[i].id.replace('msg-hl-', '');
            if (msgId) return msgId;
          }
        }
      } catch (_) {}
    }
    return viewableAnchorIdRef.current || null;
  }, [getScrollElement]);

  const handleLoadEarlierMessages = useCallback(async () => {
    if (!canLoadArchivedMessages || isLoadingArchivedMessages) return;

    // Disarm any bottom pins or follow-live immediately so the prepend doesn't yank
    followLiveRef.current = false;
    clearBottomPin();
    abortScrollRestore();

    const anchorMessageId = findTopVisibleMessageId();
    const el = getScrollElement();
    const prevOffset = el ? el.scrollTop : scrollOffsetRef.current;
    const prevHeight = el ? el.scrollHeight : contentHeightRef.current;

    prependCompensationRef.current = {
      prevHeight,
      prevOffset,
      anchorMessageId,
      until: Date.now() + 6000,
    };

    if (anchorMessageId) {
      anchorPinRef.current = {
        messageId: anchorMessageId,
        viewPosition: 0,
        until: Date.now() + 6000,
      };
    }

    try {
      await fetchArchivedMessages(id);
    } catch (loadError) {
      prependCompensationRef.current = null;
      anchorPinRef.current = null;
      addToast(loadError?.message || 'Could not load earlier messages.', 'error');
    }
  }, [addToast, canLoadArchivedMessages, fetchArchivedMessages, id, isLoadingArchivedMessages, findTopVisibleMessageId, clearBottomPin, abortScrollRestore, getScrollElement]);

  // Latest gate + runner for the handleScroll top-threshold check above.
  autoLoadEarlierRef.current = {
    can: canLoadArchivedMessages && !isLoadingArchivedMessages,
    run: handleLoadEarlierMessages,
  };

  // PLUS_WINBACK_PRESSURE §2: when the conversation detail endpoint
  // truncated the scrollback for a churned-Plus user, render the
  // gradient locked banner above the visible tail. The banner takes
  // precedence over the "Load earlier messages" archive button —
  // churned-Plus users cannot reach archived messages anyway (the
  // archive endpoint is gated separately), and showing both surfaces
  // would be confusing. When no lock applies, fall back to the normal
  // archive loader.
  const plusMessageLock = chat?.plusMessageLock;
  const showPlusLockBanner = !!plusMessageLock?.isChurnedPlus && (plusMessageLock?.hiddenCount || 0) > 0;
  // Mirror MessageGroup's column math so the locked banner + ceiling line
  // up exactly with the real message bubbles (centered, same max width)
  // instead of spilling to the full viewport width.
  const messageWidthPixels = useMemo(() => {
    const value = MESSAGE_WIDTH_AMOUNTS[messageWidth?.toUpperCase() || 'NARROW'];
    if (typeof value === 'string') return value;
    return value || 680;
  }, [messageWidth]);

  const listHeaderComponent = useMemo(() => {
    if (showPlusLockBanner) {
      // Scroll order (top → bottom): slim top bar → blurred preview →
      // boundary banner → readable messages. Scrolling up from the latest
      // messages hits the boundary banner + CTA first; continuing up through
      // the blur sees a slim re-iteration bar at the very top.
      const hasLockedPreview = (plusMessageLock.lockedPreview?.length || 0) > 0;
      return (
        <>
          {hasLockedPreview ? (
            <View style={styles.lockColumn}>
              <View style={[styles.lockColumnInner, { maxWidth: messageWidthPixels }]}>
                <PlusMessageLockBanner
                  variant="top"
                  hiddenCount={plusMessageLock.hiddenCount}
                  visibleCap={plusMessageLock.visibleCap}
                />
              </View>
            </View>
          ) : null}
          <PlusLockedMessageCeiling
            preview={plusMessageLock.lockedPreview}
            conversationId={id}
            chatTitle={chatTitle}
            isOwner={isOwner}
          />
          <View style={styles.lockColumn}>
            <View style={[styles.lockColumnInner, { maxWidth: messageWidthPixels }]}>
              <PlusMessageLockBanner
                variant="boundary"
                hiddenCount={plusMessageLock.hiddenCount}
                visibleCap={plusMessageLock.visibleCap}
              />
            </View>
          </View>
        </>
      );
    }
    if (!canLoadArchivedMessages) return null;

    return (
      <View style={styles.archiveLoaderContainer}>
        <Button
          text={isLoadingArchivedMessages ? 'Loading...' : 'Load earlier messages'}
          prependIcon="chevron-up"
          variant="secondary"
          size="small"
          isLoading={isLoadingArchivedMessages}
          isDisabled={isLoadingArchivedMessages}
          style={styles.archiveLoaderButton}
          onPress={handleLoadEarlierMessages}
        />
      </View>
    );
  }, [showPlusLockBanner, plusMessageLock?.hiddenCount, plusMessageLock?.visibleCap, plusMessageLock?.lockedPreview, messageWidthPixels, id, chatTitle, isOwner, canLoadArchivedMessages, handleLoadEarlierMessages, isLoadingArchivedMessages, styles.lockColumn, styles.lockColumnInner, styles.archiveLoaderButton, styles.archiveLoaderContainer]);

  const listFooterComponent = useMemo(() => {
    let lastItemImageRequestQuery = null;
    let lastItemMediaContext = null;
    let lastItem = null;
    if (groupedMessages.length > 0) {
      lastItem = groupedMessages[groupedMessages.length - 1];
      if (lastItem?.type === 'group') {
        lastItemImageRequestQuery = getImageRequestQueryForGroup(lastItem);
        lastItemMediaContext = getMediaGenerationContextForGroup(lastItem);
      } else if (lastItem?.role === 'assistant' && lastItem?.id) {
        lastItemImageRequestQuery = getImageRequestQueryForAssistant(lastItem);
        lastItemMediaContext = getMediaGenerationContextForAssistant(lastItem);
      }
    }

    const workSuggestion = !isCoworkerChat && isOwner && !workModeDisabled
      ? resolveWorkModeSuggestion({
        lastItem,
        messages: chatData.messages,
        isInferencing,
        archivedMessageCount,
      })
      : null;

    const forkedFrom = coworker?.task?.handoffSource ? null : chat?.forkedFrom;
    if (!lastItemImageRequestQuery && !lastItemMediaContext && !forkedFrom?.conversationId && !workSuggestion) {
      return null;
    }

    return (
      <>
        {workSuggestion ? (
          <WorkModeSuggestions
            key={workSuggestion.userMessageId || 'work-mode'}
            prompt={workSuggestion.prompt}
            fileIds={workSuggestion.fileIds}
            onSendToWork={workSuggestion.requiresChoice
              ? () => handleWorkModeChoice('work', workSuggestion.userMessageId)
              : handleSendPromptToWork}
            onContinueChat={workSuggestion.requiresChoice
              ? () => handleWorkModeChoice('chat', workSuggestion.userMessageId)
              : undefined}
            disabled={!isOwner || isInferencing}
          />
        ) : null}
        {(!hideMediaSuggestions && (lastItemMediaContext || lastItemImageRequestQuery)) ? (
          <MediaGenerationSuggestions
            mediaContext={lastItemMediaContext}
            userQuery={lastItemImageRequestQuery}
            onTryModel={handleTryMediaModel}
            canTryModels={isOwner && !isInferencing}
          />
        ) : null}
        {forkedFrom?.conversationId ? (
          <ConversationForkOrigin
            conversationId={String(forkedFrom.conversationId)}
            messageId={forkedFrom.messageId}
            title={forkedFrom.title}
            isCompact={isCompact}
          />
        ) : null}
      </>
    );
  }, [groupedMessages, id, getImageRequestQueryForAssistant, getImageRequestQueryForGroup, getMediaGenerationContextForAssistant, getMediaGenerationContextForGroup, handleTryMediaModel, hideMediaSuggestions, isOwner, isInferencing, chat?.forkedFrom, coworker?.task?.handoffSource, isCompact, chatData.messages, archivedMessageCount, isCoworkerChat, workModeDisabled, handleSendPromptToWork, handleWorkModeChoice]);

  // Memoize list props
  const flatListProps = useMemo(() => {
    const data = [...groupedMessages];

    // If the backend has compacted earlier turns into a rolling summary, surface
    // a small pill at the very top of the visible message list. This is the
    // "no silent loss" signal — the user always knows when older context has
    // been folded into a summary, and can expand it to read what was kept.
    if (hasConversationSummaryPill) {
      data.unshift({
        id: 'conversation-summary-pill',
        type: 'conversation-summary-pill',
        _stableKey: 'conversation-summary-pill',
        summary: chat.summary,
        summarizedMessageCount: chat.summarizedMessageCount || 0,
      });
    }

    const hasInferencingWaiting = data.some((item) => item.id === 'inferencing-waiting' || item.isInferencingWaiting);
    const hasGhostMessage = data.some(item => item.id === 'ghost-message');

    if (chat?.isInferencingWaiting && !hasInferencingWaiting) {
      data.push({
        id: 'inferencing-waiting',
        role: 'assistant',
        content: '',
        isInferencingWaiting: true,
        isWaiting: true,
        inferenceStatus: chat?.pendingInferenceStatus,
        inferenceStage: chat?.pendingInferenceStage,
        inferenceElapsedMs: chat?.pendingInferenceElapsedMs,
        canFastAnswer: chat?.pendingCanFastAnswer,
        conversationId: id,
      });
    }
    // Work thread: progress and anything the run needs from the user land at
    // the end of the transcript, in the order the user should read them —
    // what it is doing now, then what it is waiting on.
    if (isCoworkerChat) {
      const { question } = coworker.browserState || {};
      // Why the thread changed shape, stated once, directly under the message
      // that caused it — an upgrade only ever happens on the opening turn, so
      // the note stays anchored there as the run appends replies below. Set
      // only for an auto-upgrade; threads opened in Work mode explain nothing.
      if (chat?.wasUpgradedToWork) {
        const firstUserIndex = data.findIndex((item) => item.role === 'user');
        const notice = {
          id: 'work-upgrade-notice',
          type: 'work-upgrade-notice',
          _stableKey: 'work-upgrade-notice',
        };
        if (firstUserIndex >= 0) data.splice(firstUserIndex + 1, 0, notice);
        else data.push(notice);
      }
      // One status row at the end of the transcript for the whole run.
      // Growing text and a finished comment used to hide this — tokens
      // pause between tools, so the thread looked idle while work continued.
      // Plan card stays attached to the turn that published it (View plan /
      // Build), like a file in the thread. The composer gate owns it while
      // approval is open so it never appears twice.
      if (shouldShowWorkPinnedPlan({
        plan: coworker.plan,
        questionKind: question?.kind,
      })) {
        const planRow = { id: 'work-plan', type: 'work-plan', _stableKey: 'work-plan', plan: coworker.plan };
        data.splice(workPlanAttachIndex(data, coworker.events, {
          isQueuedMessage: isWorkFollowUpQueueMessage,
          plan: coworker.plan,
        }), 0, planRow);
      }
      if (shouldShowWorkLiveActivity({
        isActive: coworker.isActive || !!workRuns?.live,
        hasInferencingWaiting,
        events: coworker.events,
        lastAssistantText: [...(chatData.messages || [])].reverse().find((message) => message?.role === 'assistant')?.content,
      })) {
        data.push({ id: 'work-live-activity', type: 'work-live-activity', _stableKey: 'work-live-activity' });
      }
      // Film-review gates pin above the composer. Keep them out of the
      // transcript whenever that pin is on screen so the same card does not
      // stack twice. If the composer is hidden (detailed input, signed-out,
      // or a shared thread), land the box in the list instead.
      const composerPinsQuestion = !isDetailed && isAuthenticated && isOwner;
      if (videoReviewQuestion && !composerPinsQuestion) {
        data.push({
          id: videoReviewQuestion.id,
          type: 'work-question',
          _stableKey: videoReviewQuestion.id,
          question: videoReviewQuestion,
        });
      }
      // A sign-in ask takes over the composer instead of appearing here: it is
      // a form the user types into, so it belongs where typing happens.
      // Live ask_user cards sit in the composer.
    }
    if (isGhost && !hasGhostMessage) {
      data.push({ id: 'ghost-message', role: 'assistant', content: 'Please connect your email to continue your conversation.', isGhost: true });
    }
    
    const hasCardExpirationWarning = data.some(item => item.id === 'card-expiration-warning');
    if (showCardExpirationWarning && !hasCardExpirationWarning && !chat?.isInferencingWaiting) {
      data.push({ id: 'card-expiration-warning', type: 'card-expiration-warning' });
    }

    const baseProps = {
      ref: flatListRef,
      data: data,
      renderItem: renderItem,
      contentContainerStyle: listContentContainerStyle,
      ...(Platform.OS === 'web' ? { style: { overflowAnchor: isVideoStudioOpen ? 'none' : 'auto' } } : {}),
      showsVerticalScrollIndicator: showsScrollIndicator && !isSettleVeilUp,
      inverted: false,
      // 'interactive' is iOS-only; Android needs 'on-drag' for list-drag
      // keyboard dismissal (web ignores the prop either way).
      keyboardDismissMode: Platform.OS === 'ios' ? 'interactive' : 'on-drag',
      keyboardShouldPersistTaps: 'handled',
      // Work rows carry deterministic ids from the projection, so the id is
      // the key. Chat rows keep `_stableKey` for locally-created bubbles that
      // are later renamed by the server.
      keyExtractor: (item) => (
        isCoworkerChat
          ? (item.id || item._stableKey || item.key || String(item))
          : (item._stableKey || item.id || item.key || String(item))
      ),
      onScroll: handleScroll,
      onContentSizeChange: (_contentWidth, contentHeight) => {
        if (!initialLoadComplete) {
          setInitialLoadComplete(true);
        }
        // While the settle veil is up, every content reflow restarts the
        // quiet timer; the list is revealed once changes stop.
        bumpListSettleVeil();
        const prevContentHeight = contentHeightRef.current;
        contentHeightRef.current = contentHeight;
        const el = getScrollElement();
        const currentScrollY = el ? el.scrollTop : scrollOffsetRef.current;
        const currentLayoutH = el ? el.clientHeight : listLayoutHeightRef.current;
        const bottomThreshold = Math.max(100, (listPaddingBottom || 0) + 20);
        const wasAtBottom = isAtBottomRef.current || (currentLayoutH > 0 && (
          currentScrollY + currentLayoutH >= prevContentHeight - (bottomThreshold + 40)
          || prevContentHeight <= currentLayoutH
        ));
        if (currentLayoutH > 0) {
          const isAtBottom = contentHeight <= currentLayoutH || currentScrollY + currentLayoutH >= contentHeight - bottomThreshold;
          isAtBottomRef.current = isAtBottom;
        }

        // Prepend compensation: keep the viewport on the current message when
        // earlier messages are loaded above it.
        const prepend = prependCompensationRef.current;
        if (prepend && Date.now() <= prepend.until && contentHeight > prepend.prevHeight) {
          const deltaHeight = contentHeight - prepend.prevHeight;
          const adjustedOffset = prepend.prevOffset + deltaHeight;
          prependCompensationRef.current = null;
          anchorPinRef.current = null;
          scrollOffsetRef.current = adjustedOffset;
          if (el) {
            el.scrollTop = adjustedOffset;
          }
          if (flatListRef.current?.scrollToOffset) {
            flatListRef.current.scrollToOffset({ offset: adjustedOffset, animated: false });
          }
          return;
        }

        // Keep pinning to the new bottom while a pin request is active. This is
        // what lets us reach the true end of long chats whose content grows as
        // markdown / code / math / images finish rendering asynchronously. The
        // stabilize timer is reset on each growth event so the pin outlasts the
        // rendering burst but releases soon after it ends.
        // Studio remounts can snap the list to 0. Restore that only when the
        // user is not reading history or asking for the tail — otherwise they
        // hit a wall on older messages and never reach the latest Work rows.
        if (isVideoStudioOpenRef.current) {
          restoreStudioScrollIfSnapped(el ? el.scrollTop : currentScrollY, el);
        }
        if (wantsBottomRef.current && flatListRef.current) {
          if (!isVideoStudioOpenRef.current && getWorkStreamScrollMode({
            isCoworkerChat,
            hasStreamingText: !!streamingWorkRef.current?.id,
            followLive: followLiveRef.current,
          }) === WORK_STREAM_SCROLL.START) {
            wantsBottomRef.current = false;
          } else {
            pinToBottomNow(false);
            if (contentHeight > prevContentHeight) bumpStabilizeTimer();
          }
        } else if (
          followLiveRef.current
          && wasAtBottom
          && !anchorPinRef.current
          && flatListRef.current
          && contentHeight > prevContentHeight
        ) {
          // Streaming and Work activity can grow an existing row after the
          // short pin window expires. Keep the tail in view until the user
          // explicitly scrolls up (followLiveRef). Work long-replies clear
          // follow-live when they pin to the reply start instead.
          pinToBottomNow(false);
        } else if (anchorPinRef.current && flatListRef.current) {
          // A restore anchor is active: keep it pinned while async content
          // (markdown / code / images) reflows the rows above it.
          repinAnchorNow();
        }

        // Work in-place streams grow the list without moving scrollTop, so
        // handleScroll never runs to reveal jump-to-live. Recompute from the
        // post-pin offset so the control appears as soon as the reply
        // overflows, and hides again after a tail pin. Do not setState here
        // — RN Web invokes this during layout, and a Work follow-up's
        // start-pin + live row retriggered it until Maximum update depth.
        if (currentLayoutH > 0) {
          const postY = el ? el.scrollTop : currentScrollY;
          scheduleShowScrollButton(shouldShowScrollToBottomButton({
            contentHeight,
            layoutHeight: currentLayoutH,
            scrollTop: postY,
            bottomThreshold,
          }));
        }
      },
      onScrollToIndexFailed: (info) => {
        const average = info?.averageItemLength || 220;
        const index = Number.isFinite(info?.index) ? info.index : 0;
        try {
          flatListRef.current?.scrollToOffset?.({ offset: Math.max(0, average * index), animated: false });
        } catch (_) {}
        setTimeout(() => {
          try {
            flatListRef.current?.scrollToIndex?.({
              index,
              animated: false,
              viewPosition: 0.35,
            });
          } catch (_) {}
        }, 120);
      },
      onScrollBeginDrag: () => {
        hasUserDraggedRef.current = true;
        isUserScrollingRef.current = true;
        userScrollIntentUntilRef.current = Date.now() + 1000;
        followLiveRef.current = false;
        // Any user-initiated drag cancels an active bottom-pin and aborts any
        // in-flight cold-start restore (anchor pin + pending scroll request) so
        // the user can freely scroll without being yanked back to the anchor.
        if (wantsBottomRef.current) clearBottomPin();
        abortScrollRestore();
        // And if the user somehow starts dragging mid-veil, reveal immediately.
        endListSettleVeil();
      },
      // The finger lifted; momentum (if any) follows. Keep intent alive for
      // the momentum handoff so a flick that lands in the tail can re-arm.
      onScrollEndDrag: () => {
        isUserScrollingRef.current = false;
        userScrollIntentUntilRef.current = Date.now() + 1500;
      },
      onMomentumScrollBegin: () => {
        isUserScrollingRef.current = true;
      },
      onMomentumScrollEnd: () => {
        isUserScrollingRef.current = false;
        userScrollIntentUntilRef.current = Date.now() + 500;
      },
      onViewableItemsChanged: onViewableItemsChangedRef.current,
      viewabilityConfig: viewabilityConfigRef.current,
      onLayout: (event) => {
        listLayoutHeightRef.current = event.nativeEvent.layout.height;
        // Opening the video studio shrinks this column. Do not JS-pin — that
        // is the jump while Work is still streaming.
        if (isVideoStudioOpenRef.current) return;
        // Layout changes (e.g. keyboard opens) can shift content; honor any
        // active pin.
        if (wantsBottomRef.current || (followLiveRef.current && isAtBottomRef.current)) {
          if (getWorkStreamScrollMode({
            isCoworkerChat,
            hasStreamingText: !!streamingWorkRef.current?.id,
            followLive: followLiveRef.current,
          }) !== WORK_STREAM_SCROLL.START) {
            pinToBottomNow(false);
          }
        }
      },
      ListFooterComponent: listFooterComponent,
      ListHeaderComponent: listHeaderComponent,
    };

    const props = {
      ...baseProps,
      removeClippedSubviews: false,
      updateCellsBatchingPeriod: 50,
    };

    if (Platform.OS === 'android') {
      props.scrollEventThrottle = 16;
    }

    if (Platform.OS === 'web') {
      // Work rows are tall and relatively few. A normal window unmounts the
      // tail when you read the top, shrinks content height to the mounted
      // cells, and the list thinks it is already at the end — you cannot
      // reach the latest messages. RN Web also ignores disableVirtualization,
      // so keep a very large window instead.
      if (isCoworkerChat) {
        props.windowSize = 121;
        props.initialNumToRender = 40;
        props.maxToRenderPerBatch = 40;
      } else if (isLinuxDesktopWeb) {
        // Linux Chromium/Firefox often software-rasterize RN-web's View tree.
        // windowSize 101 keeps ~everything mounted and is what produced 500–800MB
        // tabs for long coding chats. Native default is 21. Mac/Windows keep the
        // large window — GPU compositing handles the extra layers, and shrinking
        // it historically left blank gaps in RN-web VirtualizedList.
        props.windowSize = 21;
        props.initialNumToRender = isCompact ? 10 : 12;
        props.maxToRenderPerBatch = isCompact ? 6 : 8;
      } else if (isCompact) {
        props.windowSize = 21;
        props.initialNumToRender = 15;
        props.maxToRenderPerBatch = 15;
      } else {
        props.windowSize = 31;
        props.initialNumToRender = 30;
        props.maxToRenderPerBatch = 30;
      }
    } else {
      props.windowSize = isXS ? 9 : 15;
      props.maxToRenderPerBatch = isXS ? 8 : 12;
      props.initialNumToRender = isXS ? 12 : 20;
    }

    // iOS maintainVisibleContentPosition is intentionally NOT set. It was the
    // root cause of long-chat viewport jolts: MVC re-pins a visible item on
    // every content reflow, which fights the bottom-pin (oscillation) and,
    // when MVC re-enables after a bottom-pin releases, snaps the viewport back
    // up toward the pinned index — the "bounces back up into the middle"
    // symptom. The bottom-pin + onContentSizeChange machinery keeps the
    // viewport at the bottom through async row growth, and archived-message
    // prepends are anchored explicitly via anchorPinRef (armed in
    // handleLoadEarlierMessages) so they no longer shift the viewport either.

    return props;
  }, [groupedMessages, listContentContainerStyle, initialLoadComplete, chat?.isInferencingWaiting, chat?.pendingInferenceStatus, chat?.pendingInferenceStage, chat?.pendingInferenceElapsedMs, chat?.pendingCanFastAnswer, chat?.summary, chat?.summarizedMessageCount, hasConversationSummaryPill, isInferencing, isXS, showsScrollIndicator, isSettleVeilUp, isCompact, isLinuxDesktopWeb, handleScroll, renderItem, isGhost, showCardExpirationWarning, id, listFooterComponent, listHeaderComponent, pinToBottomNow, clearBottomPin, restoreStudioScrollIfSnapped, bumpStabilizeTimer, repinAnchorNow, bumpListSettleVeil, endListSettleVeil, abortScrollRestore, isCoworkerChat, isVideoStudioOpen, chat?.wasUpgradedToWork, coworker.isActive, coworker.browserState, coworker.plan, coworker.events, workRuns, scheduleShowScrollButton, videoReviewQuestion, isDetailed, isAuthenticated, isOwner]);

  // A run waiting on a sign-in gets the composer to itself: the user is being
  // asked to type credentials, and they must go to the browser rather than into
  // the thread, so the form stands where the message box would be.
  const workSignInRequest = isCoworkerChat && coworker.isActive
    ? coworker.browserState?.loginRequest || null
    : null;

  // Chat → Work swaps the model picker, voice, placeholder and action slot at
  // once. Fading the wrapper through zero means the relayout lands while the
  // bar is invisible; only the result is ever seen. Opacity alone keeps the
  // text input mounted, so focus and anything half-typed survive the switch.
  const hasNewTaskResponse = !!coworker.task?.handoffSource && (
    !!coworker.browserState?.question || !!coworker.browserState?.confirmation || !!workSignInRequest
    || chatData.messages.some(message => message.role === 'assistant'
      && (String(message.content || '').trim() || message.files?.length || message.parameters?.coworkerArtifacts?.length))
  );

  const chatInputComponent = useMemo(() => {
    return (
      <ChatInputContainer
        forceInputStyle={isCompact ? CHAT_INPUT_STYLE.FIXED : undefined}
        wrapperAnimatedStyle={workHandoverStyle}
      >
        {isCoworkerChat && (
          <>
            <CoworkerLivePanel
              taskId={coworker.taskId}
              isActive={coworker.isActive}
              taskStatus={coworker.taskStatus}
              onResume={coworker.resume}
              question={coworker.browserState?.question}
            />
            <WorkTaskBoundaryCard review={coworker.handoffReview} busy={coworker.boundaryChecking}
              sourceActive={coworker.isActive} onResolve={coworker.resolveHandoff} onCancel={coworker.cancelHandoff} />
            <WorkTaskStartedNotice source={coworker.task?.handoffSource} taskId={coworker.taskId} autoDismiss={hasNewTaskResponse} />
            {!workSignInRequest && (
              <WorkFollowUpQueue
                isActive={coworker.isActive}
                items={coworker.followUpQueue}
                onEdit={coworker.updateFollowUp}
                onDelete={coworker.removeFollowUp}
                onReorder={coworker.reorderFollowUp}
                onSendNow={handleQueuedFollowUpNow}
                onClear={coworker.clearFollowUps}
              />
            )}
          </>
        )}
        {workSignInRequest ? (
          <WorkSignInComposer taskId={coworker.taskId} loginRequest={workSignInRequest} />
        ) : (
        <ChatInput
          key={id}
          onSend={handleSend}
          onCancel={handleCancelInferencing}
          showGhostBanner={isGhost}
          conversationId={id}
          collectionId={chat?.inCollectionId}
          forceInputStyle={isCompact ? CHAT_INPUT_STYLE.FIXED : undefined}
          hideVoiceMode={isCompact || isCoworkerChat}
          isWorkMode={isCoworkerChat}
          // Work owns the meaning of the composer: steer while live, request
          // a revision while idle. Never let the generic per-chat placeholder
          // cached in input state replace that critical instruction.
          forcePlaceholder={isCompact || isCoworkerChat}
          isCompact={isCompact}
          sendWhileInferencing={isCoworkerChat}
          workFollowUpMode={isCoworkerChat && coworker.isActive}
          onSendNow={handleSendNow}
          // Drive Stop from the same liveness as the follow-up queue.
          // chat.isInferencing can lag a live event log (stale idle snapshot).
          isInferencing={isCoworkerChat && coworker.isActive}
          withModels={!isCoworkerChat}
          placeholder={isCoworkerChat
            ? (coworker.isActive ? WORK_STEER_PLACEHOLDER : WORK_FOLLOWUP_PLACEHOLDER)
            : (isImageGeneration ? 'Describe what you want to see...' : 'Type a message...')}
          isDisabled={isHeldForReview}
        />
        )}
      </ChatInputContainer>
    );
  }, [id, handleSend, handleSendNow, handleQueuedFollowUpNow, handleCancelInferencing, isGhost, chat?.inCollectionId, isImageGeneration, isCompact, isHeldForReview, isCoworkerChat, workHandoverStyle, coworker.taskId, coworker.isActive, coworker.taskStatus, coworker.resume, coworker.followUpQueue, coworker.updateFollowUp, coworker.removeFollowUp, coworker.reorderFollowUp, coworker.clearFollowUps, coworker.browserState?.question, coworker.handoffReview, coworker.task?.handoffSource, coworker.boundaryChecking, coworker.resolveHandoff, coworker.cancelHandoff, workSignInRequest, hasNewTaskResponse]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <ChatLoadingState />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <Text color='primary' style={{ marginBottom: 10 }}>
            {error}
          </Text>
          <Button
            text="Go Home"
            variant="secondary"
            size="medium"
            onPress={() => router.replace('/')}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.content} testID={`conversation-view-${id}`}>
      <View style={styles.mainContent}>
        <BannerPlacement placement="chat-banner" isCompact />
        <View
          ref={messagesContainerRef}
          style={styles.messagesContainer}
          onLayout={handleMessagesAreaLayout}
        >
          <View style={[styles.transcriptContainer, { marginRight: sourcesReservedRight }]}>
            {isDetailed && (
              <View style={styles.chatInputDetailedContainer}>
                {chatInputComponent}
              </View>
            )}
            <Animated.View
              pointerEvents={isSettleVeilUp ? 'none' : 'auto'}
              style={[
                { flex: 1, opacity: listSettleOpacity },
                Platform.OS === 'web' && { minHeight: 0 },
                isSettleVeilUp && { overflow: 'hidden' },
              ]}
            >
              {/* Keyed on the thread alone: keying on Work-ness too used to
                  remount the whole list the moment a chat upgraded, throwing
                  away scroll position mid-transition. */}
              <WorkNotesVisibilityProvider key={id || 'chat'}>
                <WorkLiveProvider value={workLiveValue}>
                  {isCoworkerChat && Platform.OS === 'web' ? (
                    <WebWorkTranscriptList
                      listRef={flatListRef}
                      data={flatListProps.data}
                      renderItem={flatListProps.renderItem}
                      keyExtractor={flatListProps.keyExtractor}
                      onScroll={flatListProps.onScroll}
                      onContentSizeChange={flatListProps.onContentSizeChange}
                      onLayout={flatListProps.onLayout}
                      contentContainerStyle={flatListProps.contentContainerStyle}
                      ListHeaderComponent={flatListProps.ListHeaderComponent}
                      ListFooterComponent={flatListProps.ListFooterComponent}
                      style={flatListProps.style}
                    />
                  ) : (
                    <FlatList {...flatListProps} />
                  )}
                </WorkLiveProvider>
              </WorkNotesVisibilityProvider>
            </Animated.View>
            {isSettleVeilUp && (
              <Animated.View
                pointerEvents="none"
                style={[StyleSheet.absoluteFillObject, { opacity: settleSkeletonOpacity }]}
              >
                <ChatMessagesSkeleton />
              </Animated.View>
            )}
          </View>
          {showBrowserPanel && (
            <View style={{ width: '55%', minWidth: 360, alignSelf: 'stretch', borderLeftWidth: 1, borderLeftColor: colors.border_color_secondary }}>
              <BrowserViewer key={`${coworker.taskId}:${browserViewerWatch}`} threadId={coworker.taskId} onClose={closeBrowserViewer} initialWatch={browserViewerWatch} embedded />
            </View>
          )}
          {hasVisibleSourcesPanel && !showBrowserPanel && (
            <SourcesOutputsPanel
              conversationId={id}
              inputs={conversationInputs}
              outputs={conversationOutputs}
              sources={conversationSources}
              collectionId={chat?.inCollectionId}
              availableHeight={messagesAreaHeight}
              onOccupiedHeightChange={handleSourcesPanelHeightChange}
              cloudBrowser={workCloudBrowser}
              videoStudio={workVideoStudio}
              showTasks={isCoworkerChat && isAuthenticated && isOwner}
              canAddTask={workChatTasks.canAddTask}
              tasks={workChatTasks.tasks}
              onAddTask={workChatTasks.addTask}
              onEditTask={workChatTasks.editTask}
              onSetTaskEnabled={workChatTasks.setTaskEnabled}
              onDeleteTask={workChatTasks.removeTask}
            />
          )}
          {showTimelineRail && !showBrowserPanel && timelinePrompts.length >= 2 && (
            <ConversationTimelineRail
              prompts={timelinePrompts}
              activePromptId={activeTimelinePromptId}
              onPromptPress={handleTimelinePromptPress}
              topInset={timelineTopInset}
              bottomInset={timelineBottomInset}
            />
          )}
          {!isCompact && (
            <ConversationFindBar
              key={id}
              conversationId={id}
              messages={chatData.messages}
            />
          )}
        </View>
        {(isAuthenticated && isOwner) && (
          <View
            ref={inputContainerRef}
            style={[styles.inputContainer, sourcesReservedRight > 0 && { right: sourcesReservedRight, width: 'auto' }, showBrowserPanel && { right: '55%', width: 'auto' }]}
            onLayout={handleInputContainerLayout}
          >
            <Animated.View style={styles.scrollToBottomButtonContainer}>
              <Button
                style={styles.scrollToBottomButton}
                variant="secondary"
                size="small"
                isRound={true}
                prependIcon="arrow-down"
                text={workJumpToLiveLabel || undefined}
                textStyle={workJumpToLiveLabel ? styles.scrollToBottomLiveText : undefined}
                onPress={scrollToBottom}
                tooltip={workJumpToLiveLabel ? 'Jump to live' : 'Scroll to bottom'}
                accessibilityLabel={workJumpToLiveLabel ? `Jump to live: ${workJumpToLiveLabel}` : 'Scroll to bottom'}
              />
            </Animated.View>

            {showFollowUpsBar && !isDetailed && (
              <FollowUpSuggestions
                suggestions={followUpSuggestions.suggestions}
                onSuggestionPress={handleFollowUpSuggestionPress}
              />
            )}
            {!isDetailed && (
              <View style={styles.composerStack}>
                {Platform.OS === 'macos' && isCoworkerChat && coworker.taskId && coworker.hasBrowserActivity && (
                  <View style={workColumnStyle}>
                    <Button
                      text="Watch browser"
                      accessibilityLabel="Watch Work's browser"
                      prependIcon="window-maximize"
                      size="small"
                      variant="secondary"
                      onPress={handleOpenWorkBrowser}
                    />
                  </View>
                )}
                {isCoworkerChat && coworker.browserState?.confirmation ? (
                  <WorkAppear style={workColumnStyle} duration={220} distance={6}>
                    <WorkApprovalCard
                      taskId={coworker.taskId}
                      confirmation={coworker.browserState.confirmation}
                      onDismissQuestion={coworker.dismissPendingGate}
                      onRestoreQuestion={coworker.restorePendingGate}
                    />
                  </WorkAppear>
                ) : null}
                {isCoworkerChat && composerQuestion ? (
                  <WorkAppear style={[workColumnStyle, styles.composerGate]} duration={220} distance={6}>
                    <WorkQuestionCard
                      key={composerQuestion?.id || coworker.taskId}
                      taskId={coworker.taskId}
                      question={composerQuestion}
                      onAnswered={(text, extra) => {
                        if (String(composerQuestion?.id || '').startsWith('video-review-')) {
                          setVideoReviewHiddenGate(videoProjectReviewGate(videoStudioProject, { events: coworker.events }));
                        }
                        handleWorkQuestionAnswered(text, extra);
                      }}
                      onDismissQuestion={coworker.dismissPendingGate}
                      onRestoreQuestion={coworker.restorePendingGate}
                    />
                  </WorkAppear>
                ) : null}
                {showCollectionSuggestionBar && (
                  <CollectionSuggestionBanner
                    suggestion={collectionSuggestion}
                    isResponding={isRespondingToCollectionSuggestion}
                    onAccept={acceptCollectionSuggestion}
                    onDismiss={dismissCollectionSuggestion}
                  />
                )}
                {!isCoworkerChat && (
                  <>
                    <WorkModeIntentSuggestions
                      contextId={id}
                      variant="conversation"
                      onActivate={handleActivateWorkFromComposer}
                    />
                    <ToolIntentSuggestions contextId={id} variant="conversation" />
                  </>
                )}
                {chatInputComponent}
              </View>
            )}
          </View>
        )}
      </View>
      {/* Browser viewer: web can take control; native watches without
          interrupting the agent's browser input. */}
      {!showBrowserPanel && isCoworkerChat && !!coworker.taskId && browserViewerTaskId === coworker.taskId && (
        <BrowserViewer key={`${coworker.taskId}:${browserViewerWatch}`} threadId={coworker.taskId} onClose={closeBrowserViewer} initialWatch={browserViewerWatch} />
      )}
    </View>
  );
});

const renderChatView = props => <ChatView {...props} />;

export default function ChatViewWithSideChat(props) {
  return <SideChatLayout {...props} renderChat={renderChatView} />;
}
